<template>
  <div class="invoices wf-page wf-editor-page">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header wf-editor-header">
            <div class="row align-items-start">
              <div class="col-lg-8">
                <h3 class="page-title">{{ pageTitle }}</h3>
                <p class="text-muted mb-2">{{ pageDescription }}</p>
                <ul class="breadcrumb">
                  <li class="breadcrumb-item">
                    <router-link to="/index">Dashboard</router-link>
                  </li>
                  <li class="breadcrumb-item">
                    <router-link to="/forms">Form Builder</router-link>
                  </li>
                  <li class="breadcrumb-item active">{{ pageTitle }}</li>
                </ul>

                <div
                  v-if="!isCreate && !loading && !loadError"
                  class="wf-editor-meta"
                >
                  <span
                    class="wf-status-badge"
                    :class="'wf-status-' + statusTone"
                  >
                    <span class="wf-status-dot" aria-hidden="true"></span>
                    {{ statusLabel }}
                  </span>
                  <span class="wf-meta-item">
                    <i class="fa fa-code-fork" aria-hidden="true"></i>
                    Versi {{ schemaVersion }}
                  </span>
                  <span class="wf-meta-item">
                    <i class="fa fa-clock-o" aria-hidden="true"></i>
                    Diubah {{ lastUpdatedLabel }}
                  </span>
                </div>
              </div>

              <div class="col-lg-4 text-lg-right wf-editor-actions">
                <div
                  class="wf-save-state"
                  :class="{
                    'is-dirty': isDirty,
                    'is-saving': saving || isLifecycleBusy,
                    'is-saved':
                      !isDirty && !saving && !isCreate && !isLifecycleBusy
                  }"
                >
                  <i
                    :class="
                      saving || isLifecycleBusy
                        ? 'fa fa-spinner fa-spin'
                        : isDirty
                        ? 'fa fa-circle'
                        : 'fa fa-check-circle'
                    "
                    aria-hidden="true"
                  ></i>
                  {{ saveStateText }}
                </div>
                <div class="wf-editor-buttons">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-rounded"
                    :disabled="loading || loadError || isLifecycleBusy"
                    :title="
                      isPreviewMode
                        ? 'Kembali ke mode desain'
                        : 'Lihat form seperti yang akan dilihat pengisi form'
                    "
                    @click="togglePreview"
                  >
                    <i
                      :class="
                        isPreviewMode ? 'fa fa-pencil' : 'fa fa-eye'
                      "
                      aria-hidden="true"
                    ></i>
                    {{ isPreviewMode ? "Mode Desain" : "Preview" }}
                  </button>

                  <button
                    v-if="canPublishLifecycle"
                    type="button"
                    class="btn btn-rounded wf-publish-btn"
                    title="Publikasikan form"
                    :disabled="saving || isLifecycleBusy || loading"
                    @click="publishForm"
                  >
                    <i
                      :class="
                        lifecycleAction === 'publish'
                          ? 'fa fa-spinner fa-spin'
                          : 'fa fa-rocket'
                      "
                      aria-hidden="true"
                    ></i>
                    {{
                      lifecycleAction === "publish"
                        ? "Mempublikasikan..."
                        : "Publikasikan"
                    }}
                  </button>

                  <button
                    v-if="canDeactivateLifecycle"
                    type="button"
                    class="btn btn-rounded wf-deactivate-btn"
                    title="Nonaktifkan form"
                    :disabled="saving || isLifecycleBusy || loading"
                    @click="deactivateForm"
                  >
                    <i
                      :class="
                        lifecycleAction === 'deactivate'
                          ? 'fa fa-spinner fa-spin'
                          : 'fa fa-pause'
                      "
                      aria-hidden="true"
                    ></i>
                    {{
                      lifecycleAction === "deactivate"
                        ? "Menonaktifkan..."
                        : "Nonaktifkan"
                    }}
                  </button>

                  <button
                    type="button"
                    class="btn btn-rounded wf-primary-btn"
                    :disabled="!canSave"
                    @click="saveDraft"
                  >
                    <i
                      :class="
                        saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'
                      "
                      aria-hidden="true"
                    ></i>
                    {{ saveButtonLabel }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="loading" class="card wf-state-card">
            <div class="card-body text-center">
              <i class="fa fa-spinner fa-spin wf-state-spinner" aria-hidden="true"></i>
              <h5 class="mt-3 mb-1">Memuat form</h5>
              <p class="text-muted mb-0">
                Mengambil detail form dari server...
              </p>
            </div>
          </div>

          <div v-else-if="loadError" class="card wf-state-card">
            <div class="card-body text-center">
              <div class="wf-state-icon is-error">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
              </div>
              <h5 class="mt-3 mb-1">Form belum dapat dimuat</h5>
              <p class="text-muted wf-state-text">{{ loadErrorMessage }}</p>
              <button
                type="button"
                class="btn wf-primary-btn mr-2"
                @click="fetchFormDetail"
              >
                <i class="fa fa-refresh" aria-hidden="true"></i>
                Coba Lagi
              </button>
              <router-link to="/forms" class="btn btn-outline-secondary">
                Kembali ke Daftar Form
              </router-link>
            </div>
          </div>

          <div v-if="!loading && !loadError" class="wf-editor-tabs" role="tablist" aria-label="Pengaturan Form">
            <button
              type="button"
              class="wf-editor-tab"
              :class="{ 'is-active': activeTab === 'builder' }"
              role="tab"
              :aria-selected="activeTab === 'builder'"
              @click="setActiveTab('builder')"
            >
              <i class="fa fa-th-list" aria-hidden="true"></i>
              Susunan Form
            </button>
            <button
              type="button"
              class="wf-editor-tab"
              :class="{ 'is-active': activeTab === 'appearance' }"
              role="tab"
              :aria-selected="activeTab === 'appearance'"
              @click="setActiveTab('appearance')"
            >
              <i class="fa fa-picture-o" aria-hidden="true"></i>
              Tampilan
            </button>
            <button
              type="button"
              class="wf-editor-tab"
              :class="{ 'is-active': activeTab === 'notifications' }"
              role="tab"
              :aria-selected="activeTab === 'notifications'"
              @click="setActiveTab('notifications')"
            >
              <i class="fa fa-bell-o" aria-hidden="true"></i>
              Notifikasi
            </button>
          </div>

          <div
            v-if="!loading && !loadError && activeTab === 'builder'"
            class="wf-builder-shell"
          >
            <aside class="wf-panel wf-palette-panel">
              <div class="wf-panel-head">
                <div>
                  <span class="wf-panel-kicker">Komponen</span>
                  <h5>Tambahkan Field</h5>
                </div>
                <span class="wf-count-badge">{{ fields.length }}/30</span>
              </div>
              <p class="wf-panel-hint">
                Seret komponen ke kanvas atau klik komponen untuk
                menambahkannya.
              </p>

              <div v-if="isSchemaLocked" class="wf-lock-note">
                <i class="fa fa-lock" aria-hidden="true"></i>
                Schema form aktif terkunci.
              </div>
              <div v-else-if="isPreviewMode" class="wf-lock-note">
                <i class="fa fa-eye" aria-hidden="true"></i>
                Kembali ke mode desain untuk mengubah field.
              </div>

              <div class="wf-palette-grid">
                <button
                  v-for="item in fieldTypes"
                  :key="item.type"
                  type="button"
                  class="wf-palette-item"
                  :class="{ 'is-disabled': !canAddField }"
                  :draggable="canAddField"
                  :disabled="!canAddField"
                  :title="paletteItemTitle(item)"
                  @click="addField(item.type)"
                  @dragstart="onPaletteDragStart($event, item.type)"
                  @dragend="onDragEnd"
                >
                  <span class="wf-palette-icon">
                    <i :class="item.icon" aria-hidden="true"></i>
                  </span>
                  <span class="wf-palette-name">{{ item.label }}</span>
                  <span class="wf-palette-desc">{{ item.description }}</span>
                  <span class="wf-palette-grip" aria-hidden="true">
                    <i class="fa fa-bars"></i>
                  </span>
                </button>
              </div>

              <div class="wf-palette-footnote">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                Di perangkat sentuh, gunakan klik komponen lalu tombol naik/turun
                untuk mengatur urutan.
              </div>
            </aside>

            <main class="wf-panel wf-canvas-panel">
              <div class="wf-canvas-toolbar">
                <div>
                  <span class="wf-panel-kicker">Kanvas</span>
                  <h5>
                    {{ isPreviewMode ? "Preview Form" : "Susunan Form" }}
                  </h5>
                </div>
                <div class="wf-canvas-toolbar-right">
                  <span v-if="dragState.active" class="wf-drag-status">
                    <i class="fa fa-arrows" aria-hidden="true"></i>
                    {{ dragHintLabel }}
                  </span>
                  <span class="wf-count-badge">{{ fields.length }} field</span>
                </div>
              </div>

              <div class="wf-page-strip" v-if="pages.length">
                <div class="wf-page-tabs">
                  <button
                    v-for="(page, pageIndex) in pages"
                    :key="page.id"
                    type="button"
                    class="wf-page-tab"
                    :class="{ 'is-active': page.id === activePageId }"
                    @click="setActivePage(page.id)"
                  >
                    <span class="wf-page-tab-label">Halaman {{ pageIndex + 1 }}</span>
                    <strong>{{ page.title || "Tanpa judul" }}</strong>
                    <small>{{ pageFieldCount(page) }} field</small>
                  </button>
                  <button
                    type="button"
                    class="wf-page-add"
                    :disabled="!canAddPage"
                    @click="addPage"
                  >
                    <i class="fa fa-plus" aria-hidden="true"></i>
                    Tambah Halaman
                  </button>
                </div>
                <div class="wf-page-strip-foot">
                  <span>Maksimal 5 halaman per form.</span>
                  <span v-if="currentPage">
                    Halaman {{ currentPageIndex + 1 }} dari {{ pages.length }}
                  </span>
                </div>
                <div v-if="currentPage" class="wf-page-settings">
                  <label class="wf-page-title-field">
                    <span>Judul halaman <small>(opsional)</small></span>
                    <input
                      v-model="currentPage.title"
                      type="text"
                      class="form-control"
                      maxlength="150"
                      :disabled="!canModifyFields"
                      placeholder="Contoh: Data Diri (boleh dikosongkan)"
                    />
                  </label>
                  <div class="wf-page-actions">
                    <button
                      type="button"
                      class="wf-field-action"
                      title="Halaman sebelumnya"
                      :disabled="currentPageIndex <= 0"
                      @click="movePageSelection(-1)"
                    >
                      <i class="fa fa-arrow-left" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      class="wf-field-action"
                      title="Halaman berikutnya"
                      :disabled="currentPageIndex >= pages.length - 1"
                      @click="movePageSelection(1)"
                    >
                      <i class="fa fa-arrow-right" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      class="wf-field-action"
                      title="Pindahkan halaman ke atas"
                      :disabled="!canModifyFields || currentPageIndex <= 0"
                      @click="movePageStep(currentPage, -1)"
                    >
                      <i class="fa fa-arrow-up" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      class="wf-field-action"
                      title="Pindahkan halaman ke bawah"
                      :disabled="
                        !canModifyFields || currentPageIndex >= pages.length - 1
                      "
                      @click="movePageStep(currentPage, 1)"
                    >
                      <i class="fa fa-arrow-down" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      class="wf-field-action is-danger"
                      title="Hapus halaman"
                      :disabled="!canModifyFields || pages.length <= 1"
                      @click="removePage(currentPage)"
                    >
                      <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="isSchemaLocked" class="wf-lock-banner">
                <i class="fa fa-lock" aria-hidden="true"></i>
                <div class="wf-lock-banner-content">
                  <div>
                    <strong>Form ini sedang aktif.</strong>
                    Schema field tidak dapat diubah sebelum form dinonaktifkan.
                    Anda masih dapat memperbarui nama dan deskripsi form.
                  </div>
                  <div v-if="publicFormUrlPreview" class="wf-public-link">
                    <i class="fa fa-link" aria-hidden="true"></i>
                    <span class="wf-public-link-url">{{ publicFormUrlPreview }}</span>
                  </div>
                  <small v-if="publicFormUrlPreview" class="wf-public-link-note">
                    Halaman publik akan tersedia pada tahap berikutnya.
                  </small>
                </div>
                <button
                  v-if="publicFormUrlPreview"
                  type="button"
                  class="btn btn-sm wf-public-link-btn"
                  @click="copyCurrentPublicLink"
                >
                  <i class="fa fa-copy" aria-hidden="true"></i>
                  Salin Link
                </button>
              </div>

              <div
                class="wf-canvas-form"
                :class="{
                  'wf-canvas-form--dragging': dragState.active,
                  'wf-canvas-form--preview': isPreviewMode
                }"
                @dragover="onCanvasDragOver"
                @drop.prevent="onCanvasDrop"
              >
                <div class="wf-form-meta">
                  <template v-if="isPreviewMode">
                    <h2 class="wf-preview-title">
                      {{ formName || "Form Tanpa Nama" }}
                    </h2>
                    <p class="wf-preview-description">
                      {{
                        formDescription ||
                        "Tambahkan deskripsi agar responden memahami tujuan form."
                      }}
                    </p>
                  </template>
                  <template v-else>
                    <div class="wf-form-group wf-form-group--meta">
                      <label for="wf-form-name">
                        Nama Form <span class="wf-required">*</span>
                      </label>
                      <input
                        id="wf-form-name"
                        ref="formNameInput"
                        v-model="formName"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid': showValidation && validation.name
                        }"
                        maxlength="150"
                        placeholder="Contoh: Form Pendaftaran Pelanggan"
                      />
                      <div class="wf-input-footer">
                        <div
                          v-if="showValidation && validation.name"
                          class="wf-error"
                        >
                          {{ validation.name }}
                        </div>
                        <span class="wf-char-count">
                          {{ formName.length }}/150
                        </span>
                      </div>
                    </div>
                    <div class="wf-form-group wf-form-group--meta">
                      <label for="wf-form-description">Deskripsi</label>
                      <textarea
                        id="wf-form-description"
                        v-model="formDescription"
                        class="form-control wf-description-input"
                        :class="{
                          'is-invalid':
                            showValidation && validation.description
                        }"
                        maxlength="5000"
                        rows="3"
                        placeholder="Jelaskan tujuan form ini untuk responden..."
                      ></textarea>
                      <div class="wf-input-footer">
                        <div
                          v-if="showValidation && validation.description"
                          class="wf-error"
                        >
                          {{ validation.description }}
                        </div>
                        <span class="wf-char-count">
                          {{ formDescription.length }}/5000
                        </span>
                      </div>
                    </div>
                  </template>
                </div>

                <div
                  v-if="!currentPageFields.length && !isPreviewMode"
                  class="wf-empty-canvas"
                  :class="{ 'is-drop-target': dragState.active }"
                  @dragover.stop.prevent="onEmptyCanvasDragOver"
                  @drop.prevent.stop="onEmptyDrop"
                >
                  <div class="wf-empty-canvas-icon">
                    <i class="fa fa-plus-square-o" aria-hidden="true"></i>
                  </div>
                  <h5>Mulai susun form Anda</h5>
                  <p class="text-muted">
                    Seret komponen dari panel kiri ke area ini, atau klik
                    komponen untuk menambahkan field pertama.
                  </p>
                  <span v-if="dragState.active" class="wf-empty-drop-label">
                    Lepaskan komponen di sini
                  </span>
                </div>

                <div
                  v-else-if="!currentPageFields.length && isPreviewMode"
                  class="wf-empty-canvas wf-empty-canvas--preview"
                >
                  <div class="wf-empty-canvas-icon">
                    <i class="fa fa-inbox" aria-hidden="true"></i>
                  </div>
                  <h5>Form belum memiliki field</h5>
                  <p class="text-muted">
                    Kembali ke mode desain untuk menambahkan field.
                  </p>
                </div>

                <div v-else class="wf-fields-list">
                  <div
                    v-for="(field, index) in currentPageFields"
                    :key="field._uid"
                    class="wf-field-wrap"
                    :data-field-uid="field._uid"
                  >
                    <div
                      v-if="!isPreviewMode && dropIndex === index"
                      class="wf-drop-indicator"
                    >
                      <span>Lepaskan di sini</span>
                    </div>

                    <div
                      class="wf-field-card"
                      :class="{
                        'is-selected':
                          !isPreviewMode && selectedUid === field._uid,
                        'is-preview': isPreviewMode,
                        'is-dragging':
                          dragState.active &&
                          dragState.kind === 'field' &&
                          dragState.fieldUid === field._uid,
                        'has-validation-error':
                          showValidation && !!fieldErrorSummary(field)
                      }"
                      @click="selectField(field)"
                      @dragover="onFieldDragOver($event, index)"
                      @drop.prevent.stop="onFieldDrop($event, index)"
                    >
                      <div v-if="!isPreviewMode" class="wf-field-toolbar">
                        <div class="wf-field-toolbar-left">
                          <span
                            v-if="canModifyFields"
                            class="wf-drag-handle"
                            draggable="true"
                            role="button"
                            tabindex="0"
                            title="Seret untuk memindahkan field"
                            aria-label="Seret untuk memindahkan field"
                            @click.stop
                            @dragstart="onFieldDragStart($event, field, index)"
                            @dragend="onDragEnd"
                          >
                            <i class="fa fa-bars" aria-hidden="true"></i>
                          </span>
                          <span class="wf-field-number">{{ index + 1 }}</span>
                          <span class="wf-field-type-icon">
                            <i
                              :class="typeMeta(field.type).icon"
                              aria-hidden="true"
                            ></i>
                          </span>
                          <span class="wf-field-type-label">
                            {{ typeMeta(field.type).label }}
                          </span>
                        </div>
                        <div class="wf-field-actions">
                          <button
                            type="button"
                            class="wf-field-action"
                            title="Pindahkan ke atas"
                            aria-label="Pindahkan field ke atas"
                            :disabled="index === 0 || !canModifyFields"
                            @click.stop="moveFieldStep(field, -1)"
                          >
                            <i class="fa fa-arrow-up" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            class="wf-field-action"
                            title="Pindahkan ke bawah"
                            aria-label="Pindahkan field ke bawah"
                            :disabled="
                              index === currentPageFields.length - 1 || !canModifyFields
                            "
                            @click.stop="moveFieldStep(field, 1)"
                          >
                            <i class="fa fa-arrow-down" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            class="wf-field-action"
                            title="Duplikasi field"
                            aria-label="Duplikasi field"
                            :disabled="!canModifyFields || fields.length >= 30"
                            @click.stop="duplicateField(field)"
                          >
                            <i class="fa fa-copy" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            class="wf-field-action is-danger"
                            title="Hapus field"
                            aria-label="Hapus field"
                            :disabled="!canModifyFields"
                            @click.stop="deleteField(field)"
                          >
                            <i class="fa fa-trash" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>

                      <div class="wf-field-content">
                        <div class="wf-field-label-row">
                          <label class="wf-field-label">
                            {{ field.label || "Tanpa label" }}
                            <span
                              v-if="field.required"
                              class="wf-required"
                              title="Wajib diisi"
                            >*</span>
                          </label>
                          <span
                            v-if="!isPreviewMode && field.key"
                            class="wf-field-key"
                          >
                            {{ field.key }}
                          </span>
                        </div>

                        <FieldPreview
                          :field="field"
                          :interactive="isPreviewMode"
                          :value="previewValues[field._uid]"
                          @input="onPreviewInput(field, $event)"
                        />

                        <div
                          v-if="
                            !isPreviewMode &&
                            showValidation &&
                            fieldErrorSummary(field)
                          "
                          class="wf-field-error"
                        >
                          <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                          {{ fieldErrorSummary(field) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="!isPreviewMode"
                    class="wf-canvas-tail"
                    :class="{ 'is-drop-target': dragState.active }"
                    @dragover.stop.prevent="onTailDragOver"
                    @drop.prevent.stop="onTailDrop"
                  >
                    <span v-if="dragState.active">
                      <i class="fa fa-plus-circle" aria-hidden="true"></i>
                      Lepaskan di sini untuk menambah ke akhir
                    </span>
                    <span v-else>
                      Seret field ke sini untuk memindahkan ke urutan akhir.
                    </span>
                  </div>

                  <div
                    v-if="!isPreviewMode && dropIndex === currentPageFields.length"
                    class="wf-drop-indicator"
                  >
                    <span>Lepaskan di sini</span>
                  </div>
                </div>

                <div
                  v-if="showValidation && validation.pages"
                  class="alert alert-warning wf-general-error"
                  role="alert"
                >
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                  {{ validation.pages }}
                </div>
                <div
                  v-if="showValidation && validation.general"
                  class="alert alert-warning wf-general-error"
                  role="alert"
                >
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                  {{ validation.general }}
                </div>
              </div>
            </main>

            <aside class="wf-panel wf-props-panel">
              <div class="wf-panel-head">
                <div>
                  <span class="wf-panel-kicker">Properti</span>
                  <h5>Pengaturan Field</h5>
                </div>
                <span v-if="selectedField" class="wf-count-badge">
                  #{{ selectedFieldIndex + 1 }}
                </span>
              </div>

              <div v-if="!selectedField" class="wf-props-empty">
                <div class="wf-props-empty-icon">
                  <i class="fa fa-hand-pointer-o" aria-hidden="true"></i>
                </div>
                <h6>Belum ada field yang dipilih</h6>
                <p class="text-muted mb-0">
                  Pilih field pada kanvas untuk mengatur label, tipe, dan
                  pilihannya.
                </p>
              </div>

              <template v-else>
                <div v-if="isSchemaLocked" class="wf-lock-note">
                  <i class="fa fa-lock" aria-hidden="true"></i>
                  Form aktif harus dinonaktifkan terlebih dahulu sebelum schema
                  dapat diubah.
                </div>
                <div v-else-if="isPreviewMode" class="wf-lock-note">
                  <i class="fa fa-eye" aria-hidden="true"></i>
                  Pengaturan field dinonaktifkan di mode preview.
                </div>

                <div class="wf-form-group">
                  <label :for="'wf-field-label-' + selectedField._uid">
                    Label <span class="wf-required">*</span>
                  </label>
                  <input
                    :id="'wf-field-label-' + selectedField._uid"
                    ref="fieldLabelInput"
                    v-model="selectedField.label"
                    type="text"
                    class="form-control"
                    :class="{
                      'is-invalid':
                        showValidation && selectedFieldErrors.label
                    }"
                    maxlength="150"
                    :disabled="propertiesDisabled"
                    placeholder="Label yang dilihat responden"
                  />
                  <div
                    v-if="showValidation && selectedFieldErrors.label"
                    class="wf-error"
                  >
                    {{ selectedFieldErrors.label }}
                  </div>
                </div>

                <div class="wf-form-group">
                  <label :for="'wf-field-type-' + selectedField._uid">
                    Tipe Field
                  </label>
                  <select
                    :id="'wf-field-type-' + selectedField._uid"
                    class="form-control"
                    :value="selectedField.type"
                    :disabled="propertiesDisabled"
                    @change="changeSelectedFieldType($event.target.value)"
                  >
                    <option
                      v-for="item in fieldTypes"
                      :key="item.type"
                      :value="item.type"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                  <small class="text-muted">
                    {{ typeMeta(selectedField.type).description }}
                  </small>
                </div>

                <div class="wf-form-group">
                  <label :for="'wf-field-key-' + selectedField._uid">
                    Key Field <span class="wf-required">*</span>
                  </label>
                  <div class="wf-input-action">
                    <input
                      :id="'wf-field-key-' + selectedField._uid"
                      v-model="selectedField.key"
                      type="text"
                      class="form-control"
                      :class="{
                        'is-invalid':
                          showValidation && selectedFieldErrors.key
                      }"
                      maxlength="50"
                      :disabled="propertiesDisabled"
                      placeholder="contoh: nama_lengkap"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :disabled="propertiesDisabled"
                      title="Buat key dari label"
                      @click="regenerateSelectedKey"
                    >
                      Dari label
                    </button>
                  </div>
                  <div
                    v-if="showValidation && selectedFieldErrors.key"
                    class="wf-error"
                  >
                    {{ selectedFieldErrors.key }}
                  </div>
                  <small class="text-muted">
                    Key tidak berubah saat field dipindahkan.
                  </small>
                </div>

                <div v-if="!isChoiceField" class="wf-form-group">
                  <label :for="'wf-field-placeholder-' + selectedField._uid">
                    Placeholder
                  </label>
                  <input
                    :id="'wf-field-placeholder-' + selectedField._uid"
                    v-model="selectedField.placeholder"
                    type="text"
                    class="form-control"
                    :class="{
                      'is-invalid':
                        showValidation && selectedFieldErrors.placeholder
                    }"
                    maxlength="255"
                    :disabled="propertiesDisabled"
                    placeholder="Petunjuk singkat di dalam input"
                  />
                  <div
                    v-if="showValidation && selectedFieldErrors.placeholder"
                    class="wf-error"
                  >
                    {{ selectedFieldErrors.placeholder }}
                  </div>
                </div>

                <div class="wf-form-group">
                  <label class="wf-switch-row">
                    <span>
                      Wajib diisi
                      <small class="text-muted d-block">
                        Responden harus mengisi field ini.
                      </small>
                    </span>
                    <span class="wf-switch">
                      <input
                        v-model="selectedField.required"
                        type="checkbox"
                        :disabled="propertiesDisabled"
                      />
                      <span class="wf-switch-slider"></span>
                    </span>
                  </label>
                </div>

                <div v-if="isChoiceField" class="wf-options-section">
                  <div class="wf-options-head">
                    <label>
                      Pilihan <span class="wf-required">*</span>
                    </label>
                    <span class="wf-count-badge">
                      {{ selectedField.options.length }}/20
                    </span>
                  </div>

                  <div
                    v-for="(option, optionIndex) in selectedField.options"
                    :key="option._uid"
                    class="wf-option-card"
                  >
                    <div class="wf-option-head">
                      <span>Opsi {{ optionIndex + 1 }}</span>
                      <button
                        type="button"
                        class="wf-field-action is-danger"
                        title="Hapus pilihan"
                        aria-label="Hapus pilihan"
                        :disabled="
                          propertiesDisabled || selectedField.options.length <= 1
                        "
                        @click="removeOption(selectedField, option)"
                      >
                        <i class="fa fa-trash" aria-hidden="true"></i>
                      </button>
                    </div>

                    <label :for="'wf-option-label-' + option._uid">
                      Teks pilihan
                    </label>
                    <input
                      :id="'wf-option-label-' + option._uid"
                      v-model="option.label"
                      type="text"
                      class="form-control"
                      :class="{
                        'is-invalid':
                          showValidation && optionError(option, 'label')
                      }"
                      maxlength="150"
                      :disabled="propertiesDisabled"
                      placeholder="Contoh: Produk A"
                    />
                    <div
                      v-if="showValidation && optionError(option, 'label')"
                      class="wf-error"
                    >
                      {{ optionError(option, "label") }}
                    </div>

                    <label
                      :for="'wf-option-value-' + option._uid"
                      class="wf-option-value-label"
                    >
                      Nilai
                    </label>
                    <input
                      :id="'wf-option-value-' + option._uid"
                      v-model="option.value"
                      type="text"
                      class="form-control"
                      :class="{
                        'is-invalid':
                          showValidation && optionError(option, 'value')
                      }"
                      maxlength="100"
                      :disabled="propertiesDisabled"
                      placeholder="contoh: produk_a"
                    />
                    <div
                      v-if="showValidation && optionError(option, 'value')"
                      class="wf-error"
                    >
                      {{ optionError(option, "value") }}
                    </div>
                  </div>

                  <div
                    v-if="showValidation && selectedFieldErrors.options"
                    class="wf-error"
                  >
                    {{ selectedFieldErrors.options }}
                  </div>

                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-block"
                    :disabled="
                      propertiesDisabled ||
                      selectedField.options.length >= 20
                    "
                    @click="addOption(selectedField)"
                  >
                    <i class="fa fa-plus" aria-hidden="true"></i>
                    Tambah Pilihan
                  </button>
                  <small class="text-muted d-block mt-2">
                    Minimal 1 dan maksimal 20 pilihan. Nilai pilihan harus unik.
                  </small>
                </div>
              </template>
            </aside>
          </div>

          <div
            v-if="!loading && !loadError && activeTab === 'appearance'"
            class="wf-settings-shell"
          >
            <div class="card wf-settings-card">
              <div class="wf-settings-head">
                <div>
                  <span class="wf-panel-kicker">Tampilan</span>
                  <h4>Gambar Form</h4>
                  <p class="text-muted mb-0">
                    Tambahkan gambar pendukung untuk memperjelas identitas form.
                  </p>
                </div>
              </div>

              <div class="wf-settings-body">
                <div class="wf-image-layout">
                  <div class="wf-image-preview-box">
                    <img
                      v-if="formImageUrl && !imagePreviewError"
                      :src="formImageUrl"
                      alt="Gambar form"
                      class="wf-form-image-preview"
                      @error="imagePreviewError = true"
                    />
                    <div v-else class="wf-image-placeholder">
                      <i class="fa fa-picture-o" aria-hidden="true"></i>
                      <span>Belum ada gambar form</span>
                    </div>
                    <div v-if="uploadingImage" class="wf-image-overlay">
                      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                      <span>Mengunggah gambar...</span>
                    </div>
                  </div>

                  <div class="wf-image-controls">
                    <h5>{{ formImageUrl ? "Ganti Gambar" : "Pilih Gambar" }}</h5>
                    <p class="text-muted">
                      Gunakan gambar JPG, PNG, WebP, atau GIF dengan ukuran
                      maksimal 2 MB.
                    </p>
                    <input
                      ref="imageInput"
                      type="file"
                      class="d-none"
                      accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                      @change="onImageSelected"
                    />
                    <button
                      type="button"
                      class="btn wf-primary-btn"
                      :disabled="!formId || uploadingImage"
                      @click="chooseImage"
                    >
                      <i
                        :class="
                          uploadingImage
                            ? 'fa fa-spinner fa-spin'
                            : 'fa fa-upload'
                        "
                        aria-hidden="true"
                      ></i>
                      {{ uploadingImage ? "Mengunggah..." : formImageUrl ? "Ganti Gambar" : "Pilih Gambar" }}
                    </button>
                    <p v-if="!formId" class="wf-settings-hint">
                      Simpan form sebagai draft terlebih dahulu sebelum
                      mengunggah gambar.
                    </p>
                    <p v-else-if="imageSelectedName" class="wf-file-meta">
                      {{ imageSelectedName }}
                      <span v-if="imageSizeLabel">({{ imageSizeLabel }})</span>
                    </p>
                    <div v-if="imageUploadError" class="alert alert-warning wf-settings-alert" role="alert">
                      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                      <span>{{ imageUploadError }}</span>
                    </div>
                    <div v-if="imageUploadSuccess" class="alert alert-success wf-settings-alert" role="alert">
                      <i class="fa fa-check-circle" aria-hidden="true"></i>
                      <span>{{ imageUploadSuccess }}</span>
                    </div>
                    <small class="wf-settings-hint">
                      Gambar akan dikonversi dan disimpan oleh server. Gambar
                      lama dipertahankan jika upload gagal.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!loading && !loadError && activeTab === 'notifications'"
            class="wf-settings-shell"
          >
            <div v-if="!formId" class="card wf-settings-card wf-settings-message">
              <div class="wf-state-icon">
                <i class="fa fa-bell-o" aria-hidden="true"></i>
              </div>
              <h5>Simpan form terlebih dahulu</h5>
              <p class="text-muted mb-0">
                Pengaturan notifikasi tersedia setelah form disimpan sebagai
                draft.
              </p>
            </div>
            <div v-else-if="notificationLoading" class="card wf-settings-card wf-settings-message">
              <i class="fa fa-spinner fa-spin wf-state-spinner" aria-hidden="true"></i>
              <p class="text-muted mb-0">Memuat pengaturan notifikasi...</p>
            </div>
            <div v-else-if="notificationLoadError" class="card wf-settings-card wf-settings-message">
              <div class="wf-state-icon is-error">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
              </div>
              <h5>Pengaturan notifikasi belum dapat dimuat</h5>
              <p class="text-muted">{{ notificationLoadErrorMessage }}</p>
              <button type="button" class="btn wf-primary-btn" @click="loadNotificationSettings">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                Coba Lagi
              </button>
            </div>
            <template v-else>
              <div class="wf-notification-header">
                <div>
                  <span class="wf-panel-kicker">WhatsApp</span>
                  <h5>Notifikasi WhatsApp</h5>
                  <p class="text-muted mb-0">
                    Maksimal 3 konfigurasi notifikasi per form. Setiap
                    konfigurasi memiliki penerima dan template sendiri.
                  </p>
                </div>
                <span class="wf-count-badge">
                  {{ notificationConfig.notifications.length }}/3
                </span>
              </div>

              <div v-if="licensesError" class="alert alert-warning wf-settings-alert" role="alert">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>{{ licensesError }}</span>
              </div>
              <div v-if="!respondentWhatsAppFields.length" class="alert alert-warning wf-settings-alert" role="alert">
                <i class="fa fa-whatsapp" aria-hidden="true"></i>
                <span>
                  Notifikasi berbasis field memerlukan minimal satu field
                  bertipe Nomor WhatsApp. Notifikasi owner tetap dapat
                  digunakan tanpa field tersebut.
                </span>
              </div>

              <div
                v-for="(rule, index) in notificationConfig.notifications"
                :key="rule._uid || rule.id || index"
                class="card wf-notification-card mb-2"
              >
                <div class="wf-notification-head">
                  <div>
                    <span class="wf-panel-kicker">Konfigurasi {{ index + 1 }}</span>
                    <h4>
                      Notifikasi {{ index + 1 }}
                      <small class="wf-notification-type-label">
                        {{ notificationRecipientLabel(rule.recipient_type) }}
                      </small>
                    </h4>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="custom-control custom-switch mr-3">
                      <input
                        :id="'wf-notification-enabled-' + index"
                        v-model="rule.enabled"
                        type="checkbox"
                        class="custom-control-input"
                        :disabled="notificationSaving"
                      />
                      <label class="custom-control-label" :for="'wf-notification-enabled-' + index">
                        Aktif
                      </label>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      :disabled="notificationSaving"
                      @click="removeNotification(index)"
                    >
                      <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                <div class="wf-notification-body">
                  <div
                    v-if="rule.recipient_type === 'respondent'"
                    class="wf-notification-recipient-note"
                  >
                    <i class="fa fa-mobile" aria-hidden="true"></i>
                    <span>
                      Konfigurasi legacy pengisi form dipertahankan. Hapus
                      konfigurasi ini jika ingin memakai Owner Form atau Field
                      Nomor WhatsApp.
                    </span>
                  </div>

                  <div
                    class="wf-notification-config-grid"
                    :class="{ 'is-single': rule.recipient_type === 'respondent' }"
                  >
                    <div v-if="rule.recipient_type !== 'respondent'" class="form-group">
                      <label>Jenis penerima</label>
                      <select
                        v-model="rule.recipient_type"
                        class="form-control"
                        :disabled="notificationSaving"
                        @change="onNotificationRecipientTypeChange(rule)"
                      >
                        <option
                          v-for="recipient in notificationRecipientOptions"
                          :key="recipient.value"
                          :value="recipient.value"
                          :disabled="
                            recipient.value === 'owner' &&
                            notificationOwnerRuleCount > 0 &&
                            rule.recipient_type !== 'owner'
                          "
                        >
                          {{ recipient.label }}
                        </option>
                      </select>
                    </div>

                    <div class="form-group">
                      <label>Nomor WhatsApp pengirim</label>
                      <select
                        v-model="rule.license_id"
                        class="form-control"
                        :disabled="!rule.enabled || licensesLoading || notificationSaving"
                      >
                        <option value="">Pilih nomor pengirim</option>
                        <option
                          v-for="license in licenseOptions"
                          :key="license.id"
                          :value="license.id"
                        >
                          {{ licenseOptionLabel(license) }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div v-if="notificationUsesField(rule.recipient_type)" class="form-group">
                    <label>Field nomor WhatsApp penerima</label>
                    <select
                      v-model="rule.recipient_field"
                      class="form-control"
                      :disabled="!rule.enabled || notificationSaving"
                    >
                      <option value="">Pilih field WhatsApp</option>
                      <option
                        v-for="field in respondentWhatsAppFields"
                        :key="field.key"
                        :value="field.key"
                      >
                        {{ field.label }} ({{ field.key }})
                      </option>
                    </select>
                    <small class="text-muted">
                      Semua field WhatsApp tetap tersedia untuk setiap
                      konfigurasi.
                    </small>
                  </div>

                  <div
                    v-else-if="rule.recipient_type === 'owner'"
                    class="wf-notification-recipient-note"
                    :class="{ 'is-invalid': !notificationConfig.owner_recipient_valid }"
                  >
                    <i class="fa fa-user-circle-o" aria-hidden="true"></i>
                    <span>{{ notificationOwnerRecipientLabel() }}</span>
                  </div>

                  <div class="form-group mb-0 wf-notification-template-group">
                    <div class="wf-template-head">
                      <label>Template pesan</label>
                      <button
                        type="button"
                        class="wf-template-reset"
                        title="Buat ulang template default"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="restoreNotificationDefaultTemplate(rule)"
                      >
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        Template Default
                      </button>
                    </div>

                    <div class="wf-wa-format-toolbar">
                      <span class="wf-template-tokens-label">Format:</span>
                      <button
                        type="button"
                        class="wf-wa-format-btn is-bold"
                        title="Bold (*teks*)"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="applyNotificationFormat(rule, '*')"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        class="wf-wa-format-btn is-italic"
                        title="Italic (_teks_)"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="applyNotificationFormat(rule, '_')"
                      >
                        I
                      </button>
                      <button
                        type="button"
                        class="wf-wa-format-btn is-strike"
                        title="Strikethrough (~teks~)"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="applyNotificationFormat(rule, '~')"
                      >
                        S
                      </button>
                      <button
                        type="button"
                        class="wf-wa-format-btn is-mono"
                        title="Monospace (```teks```)"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="applyNotificationFormat(rule, '```')"
                      >
                        M
                      </button>
                    </div>

                    <div class="wf-template-tokens">
                      <span class="wf-template-tokens-label">Sisipkan:</span>
                      <button
                        type="button"
                        class="wf-template-token"
                        title="Sisipkan placeholder form name"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="insertNotificationPlaceholder(rule, 'form_name')"
                      >
                        Form name
                      </button>
                      <button
                        v-for="field in schemaNotificationFields"
                        :key="'token-' + rule._uid + '-' + field.key"
                        type="button"
                        class="wf-template-token"
                        :title="'Sisipkan {{' + field.key + '}}'"
                        :disabled="!rule.enabled || notificationSaving"
                        @click.prevent="insertNotificationPlaceholder(rule, field.key)"
                      >
                        {{ field.label }}
                      </button>
                    </div>
                    <textarea
                      :id="notificationTemplateElementId(rule)"
                      v-model="rule.template"
                      class="form-control wf-notification-textarea"
                      rows="4"
                      maxlength="5000"
                      :disabled="!rule.enabled || notificationSaving"
                      :placeholder="notificationTemplatePlaceholder"
                    ></textarea>
                    <div class="wf-input-footer">
                      <small class="text-muted">
                        Placeholder:
                        <code>&#123;&#123;form_name&#125;&#125;</code>
                        dan
                        <code>&#123;&#123;field_key&#125;&#125;</code>.
                        Klik tombol untuk menyisipkan di posisi kursor.
                      </small>
                      <span class="wf-char-count">{{ (rule.template || "").length }}/5000</span>
                    </div>

                    <div v-if="rule.template" class="wf-wa-preview">
                      <span class="wf-wa-preview-label">
                        <i class="fa fa-whatsapp" aria-hidden="true"></i>
                        Pratinjau pesan
                      </span>
                      <div class="wf-wa-bubble">
                        <div
                          class="wf-wa-text"
                          v-html="notificationPreviewHtml(rule)"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!notificationConfig.notifications.length" class="wf-settings-empty">
                <i class="fa fa-bell-o" aria-hidden="true"></i>
                <p>Belum ada konfigurasi notifikasi.</p>
              </div>

              <button
                type="button"
                class="btn btn-outline-secondary wf-add-notification"
                :disabled="
                  notificationSaving ||
                  notificationConfig.notifications.length >= 3 ||
                  (!respondentWhatsAppFields.length && notificationOwnerRuleCount > 0)
                "
                @click="addNotification"
              >
                <i class="fa fa-plus" aria-hidden="true"></i>
                Tambah Notifikasi
              </button>

              <div v-if="notificationError" class="alert alert-warning wf-settings-alert" role="alert">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>{{ notificationError }}</span>
              </div>
              <div v-if="notificationSuccess" class="alert alert-success wf-settings-alert" role="alert">
                <i class="fa fa-check-circle" aria-hidden="true"></i>
                <span>{{ notificationSuccess }}</span>
              </div>

              <div class="wf-settings-actions">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  :disabled="notificationSaving || !notificationDirty"
                  @click="cancelNotificationChanges"
                >
                  Batal
                </button>
                <button
                  type="button"
                  class="btn wf-primary-btn"
                  :disabled="notificationSaving || !notificationDirty || licensesLoading"
                  @click="saveNotificationSettings"
                >
                  <i
                    :class="notificationSaving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"
                    aria-hidden="true"
                  ></i>
                  {{ notificationSaving ? "Menyimpan..." : "Simpan Pengaturan" }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";
import FieldPreview from "@/views/forms/components/FieldPreview.vue";
import { formatLocalDate } from "@/utils/helper";
import {
  extractBackendMessage,
  getHttpStatus,
  getWaajoFormsErrorMessage
} from "@/utils/waajoFormsError";
import {
  canDeactivateForm,
  canPublishForm,
  copyTextToClipboard,
  getFormStatusLabel,
  getFormStatusTone,
  getPublicFormUrl,
  isLifecycleConflict,
  isLifecycleRetryable
} from "@/utils/waajoFormsLifecycle";
import {
  FIELD_TYPES,
  MAX_FIELDS,
  MAX_NOTIFICATIONS,
  MAX_NOTIFICATION_TEMPLATE,
  MAX_OPTIONS,
  MAX_PAGES,
  buildFormPayload,
  createDefaultOptions,
  createField,
  createOption,
  createPage,
  createUid,
  getFieldTypeMeta,
  hasValidationErrors,
  isChoiceType,
  makeUniqueKey,
  makeUniqueOptionValue,
  normalizeFieldFromServer,
  normalizePagesFromServer,
  validateForm
} from "@/utils/waajoFormsSchema";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif"];

function emptyNotificationConfig() {
  return {
    form_id: "",
    owner: {
      enabled: false,
      license_id: ""
    },
    respondent: {
      enabled: false,
      license_id: "",
      fields: []
    },
    notifications: [],
    owner_recipient: "",
    owner_recipient_valid: false,
    updated_at: 0
  };
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

export default {
  name: "waajo-forms-editor",
  components: {
    LayoutHeader,
    LayoutSidebar,
    FieldPreview
  },
  data() {
    return {
      loading: false,
      loadError: false,
      loadErrorMessage: "",
      saving: false,
      lifecycleAction: "",
      formId: "",
      formName: "",
      formDescription: "",
      formStatus: "",
      schemaVersion: 0,
      updateDatetime: 0,
      fields: [],
      pages: [],
      activePageId: "",
      selectedUid: "",
      canvasMode: "design",
      activeTab: "builder",
      previewValues: {},
      formImageUrl: "",
      imagePreviewError: false,
      uploadingImage: false,
      imageUploadError: "",
      imageUploadSuccess: "",
      imageSelectedName: "",
      imageSelectedSize: 0,
      notificationConfig: emptyNotificationConfig(),
      notificationSaved: emptyNotificationConfig(),
      notificationRecipientOptions: [
        { value: "owner", label: "Owner Form" },
        { value: "field", label: "Field Nomor WhatsApp" }
      ],
      notificationLoading: false,
      notificationLoadError: false,
      notificationLoadErrorMessage: "",
      notificationsLoaded: false,
      notificationSaving: false,
      notificationError: "",
      notificationSuccess: "",
      licenseOptions: [],
      licensesLoading: false,
      licensesError: "",
      dragState: {
        active: false,
        kind: "",
        type: "",
        fieldUid: ""
      },
      dropIndex: -1,
      showValidation: false,
      savedSnapshot: "",
      allowLeave: false,
      sessionExpired: false,
      notificationTabTransitioning: false
    };
  },
  computed: {
    isCreate() {
      return this.$route && this.$route.name === "waajo_forms_create";
    },
    pageTitle() {
      return this.isCreate ? "Buat Form" : "Edit Form";
    },
    pageDescription() {
      return this.isCreate
        ? "Rancang form baru dengan editor visual, lalu simpan sebagai draft."
        : "Perbarui susunan field dan pengaturan form.";
    },
    isSchemaLocked() {
      return this.formStatus === "active";
    },
    canPublishLifecycle() {
      return (
        !this.isCreate &&
        !this.loading &&
        !this.loadError &&
        canPublishForm(this.formStatus)
      );
    },
    canDeactivateLifecycle() {
      return (
        !this.isCreate &&
        !this.loading &&
        !this.loadError &&
        canDeactivateForm(this.formStatus)
      );
    },
    isLifecycleBusy() {
      return !!this.lifecycleAction;
    },
    publicFormUrlPreview() {
      if (!this.formId || !this.isSchemaLocked) {
        return "";
      }
      return getPublicFormUrl(this.formId);
    },
    isPreviewMode() {
      return this.canvasMode === "preview";
    },
    currentPage() {
      return (
        this.pages.find(page => page.id === this.activePageId) ||
        this.pages[0] ||
        null
      );
    },
    currentPageIndex() {
      return this.pages.findIndex(page => page.id === this.activePageId);
    },
    currentPageFields() {
      if (!this.activePageId) {
        return [];
      }
      return this.fields.filter(field => field.page_id === this.activePageId);
    },
    canAddPage() {
      return this.canModifyFields && this.pages.length < MAX_PAGES;
    },
    schemaNotificationFields() {
      return this.fields
        .filter(function(field) {
          return field && field.key;
        })
        .map(function(field) {
          return {
            key: field.key,
            label: field.label || field.key,
            type: field.type
          };
        });
    },
    respondentWhatsAppFields() {
      return this.schemaNotificationFields.filter(function(field) {
        return field.type === "whatsapp";
      });
    },
    notificationOwnerRuleCount() {
      return this.notificationConfig.notifications.filter(function(rule) {
        return rule && rule.recipient_type === "owner";
      }).length;
    },
    notificationTemplatePlaceholder() {
      return 'Contoh: Form "{{form_name}}" menerima respons baru. Nama: {{nama}}';
    },
    missingNotificationFields() {
      const available = {};
      this.schemaNotificationFields.forEach(function(field) {
        available[field.key] = true;
      });
      return this.notificationConfig.respondent.fields.filter(function(key) {
        return !available[key];
      });
    },
    notificationDirty() {
      if (!this.notificationsLoaded) {
        return false;
      }
      return (
        JSON.stringify(this.notificationConfig) !==
        JSON.stringify(this.notificationSaved)
      );
    },
    ownerLicenseInvalid() {
      if (!this.notificationConfig.owner.enabled) {
        return false;
      }
      if (!this.notificationConfig.owner.license_id) {
        return true;
      }
      if (this.licensesLoading) {
        return false;
      }
      return !this.licenseOptions.some(
        license => license.id === this.notificationConfig.owner.license_id
      );
    },
    respondentLicenseInvalid() {
      if (!this.notificationConfig.respondent.enabled) {
        return false;
      }
      if (!this.notificationConfig.respondent.license_id) {
        return true;
      }
      if (this.licensesLoading) {
        return false;
      }
      return !this.licenseOptions.some(
        license =>
          license.id === this.notificationConfig.respondent.license_id
      );
    },
    ownerRecipientInvalid() {
      return (
        this.notificationConfig.owner.enabled &&
        !this.notificationConfig.owner_recipient_valid
      );
    },
    notificationPreviewOwner() {
      return (
        'Form "' +
        (this.formName || "Form Tanpa Nama") +
        '" baru saja menerima respons baru.'
      );
    },
    notificationPreviewRespondent() {
      return {
        intro:
          'Terima kasih telah mengisi form "' +
          (this.formName || "Form Tanpa Nama") +
          '".',
        lines: this.notificationConfig.respondent.fields.map(
          key => {
            const field = this.schemaNotificationFields.find(
              item => item.key === key
            );
            return (field ? field.label : key) + ": [jawaban]";
          }
        )
      };
    },
    imageSizeLabel() {
      if (!this.imageSelectedSize) {
        return "";
      }
      return this.formatFileSize(this.imageSelectedSize);
    },
    canModifyFields() {
      return !this.isSchemaLocked && !this.isPreviewMode && !this.loading;
    },
    canAddField() {
      return this.canModifyFields && this.fields.length < MAX_FIELDS;
    },
    propertiesDisabled() {
      return this.isSchemaLocked || this.isPreviewMode;
    },
    fieldTypes() {
      return FIELD_TYPES;
    },
    selectedField() {
      if (!this.selectedUid) {
        return null;
      }
      for (let index = 0; index < this.fields.length; index += 1) {
        if (this.fields[index]._uid === this.selectedUid) {
          return this.fields[index];
        }
      }
      return null;
    },
    selectedFieldIndex() {
      if (!this.selectedField) {
        return -1;
      }
      return this.fields.findIndex(field => field._uid === this.selectedUid);
    },
    isChoiceField() {
      return !!(this.selectedField && isChoiceType(this.selectedField.type));
    },
    statusLabel() {
      return getFormStatusLabel(this.formStatus);
    },
    statusTone() {
      return getFormStatusTone(this.formStatus);
    },
    lastUpdatedLabel() {
      const unix = Number(this.updateDatetime);
      if (!unix || !isFinite(unix) || unix <= 0) {
        return "-";
      }
      const date = new Date(unix * 1000);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return formatLocalDate(unix);
    },
    currentSnapshot() {
      return this.buildSnapshot();
    },
    isDirty() {
      return (
        this.savedSnapshot !== "" && this.currentSnapshot !== this.savedSnapshot
      );
    },
    validation() {
      return validateForm({
        name: this.formName,
        description: this.formDescription,
        fields: this.fields,
        pages: this.pages,
        includeFields: !this.isSchemaLocked
      });
    },
    selectedFieldErrors() {
      if (!this.selectedField) {
        return {
          label: "",
          key: "",
          placeholder: "",
          options: "",
          optionErrors: {}
        };
      }
      return (
        this.validation.fields[this.selectedField._uid] || {
          label: "",
          key: "",
          placeholder: "",
          options: "",
          optionErrors: {}
        }
      );
    },
    canSave() {
      if (
        this.saving ||
        this.loading ||
        this.loadError ||
        this.isLifecycleBusy
      ) {
        return false;
      }
      return this.isCreate || this.isDirty;
    },
    saveButtonLabel() {
      if (this.saving) {
        return "Menyimpan...";
      }
      return this.isSchemaLocked ? "Simpan Perubahan" : "Simpan Draft";
    },
    saveStateText() {
      if (this.lifecycleAction === "publish") {
        return "Mempublikasikan form...";
      }
      if (this.lifecycleAction === "deactivate") {
        return "Menonaktifkan form...";
      }
      if (this.saving) {
        return "Menyimpan perubahan...";
      }
      if (this.isDirty) {
        return "Perubahan belum disimpan";
      }
      if (this.isCreate) {
        return "Belum disimpan";
      }
      return "Semua perubahan tersimpan";
    },
    dragHintLabel() {
      if (!this.dragState.active) {
        return "";
      }
      if (this.dragState.kind === "palette") {
        return "Lepaskan komponen untuk menambah field";
      }
      return "Lepaskan untuk memindahkan field";
    }
  },
  watch: {
    "$route.path"() {
      this.initializePage();
    }
  },
  created() {
    this.initializePage();
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.isLifecycleBusy) {
      return next(false);
    }
    if (this.isDirty && !this.allowLeave && !this.saving) {
      const confirmed = window.confirm(
        "Perubahan form belum disimpan. Tinggalkan halaman ini?"
      );
      if (!confirmed) {
        return next(false);
      }
    }
    if (this.notificationDirty && !this.allowLeave && !this.notificationSaving) {
      const confirmed = window.confirm(
        "Perubahan notifikasi belum disimpan. Tinggalkan halaman ini?"
      );
      if (!confirmed) {
        return next(false);
      }
    }
    next();
  },
  methods: {
    initializePage() {
      this.resetState();
      if (this.isCreate) {
        this.setupCreateForm();
      } else {
        this.fetchFormDetail();
      }
    },
    resetState() {
      this.loading = false;
      this.loadError = false;
      this.loadErrorMessage = "";
      this.saving = false;
      this.lifecycleAction = "";
      this.formId = "";
      this.formName = "";
      this.formDescription = "";
      this.formStatus = "";
      this.schemaVersion = 0;
      this.updateDatetime = 0;
      this.fields = [];
      this.pages = [];
      this.activePageId = "";
      this.selectedUid = "";
      this.canvasMode = "design";
      this.activeTab = "builder";
      this.previewValues = {};
      this.formImageUrl = "";
      this.imagePreviewError = false;
      this.uploadingImage = false;
      this.imageUploadError = "";
      this.imageUploadSuccess = "";
      this.imageSelectedName = "";
      this.imageSelectedSize = 0;
      this.notificationConfig = emptyNotificationConfig();
      this.notificationSaved = emptyNotificationConfig();
      this.notificationLoading = false;
      this.notificationLoadError = false;
      this.notificationLoadErrorMessage = "";
      this.notificationsLoaded = false;
      this.notificationSaving = false;
      this.notificationError = "";
      this.notificationSuccess = "";
      this.licenseOptions = [];
      this.licensesLoading = false;
      this.licensesError = "";
      this.dragState = {
        active: false,
        kind: "",
        type: "",
        fieldUid: ""
      };
      this.dropIndex = -1;
      this.showValidation = false;
      this.savedSnapshot = "";
      this.allowLeave = false;
      this.sessionExpired = false;
      this.notificationTabTransitioning = false;
    },
    async setActiveTab(tab) {
      if (
        this.loading ||
        this.loadError ||
        this.notificationTabTransitioning ||
        this.saving
      ) {
        return;
      }
      if (["builder", "appearance", "notifications"].indexOf(tab) === -1) {
        return;
      }
      if (tab === this.activeTab) {
        return;
      }
      if (tab === "notifications" && this.isDirty) {
        await this.confirmSaveBeforeNotifications();
        return;
      }
      this.activateTab(tab);
    },
    activateTab(tab) {
      this.activeTab = tab;
      if (
        tab === "notifications" &&
        this.formId &&
        !this.notificationsLoaded &&
        !this.notificationLoading
      ) {
        this.loadNotificationSettings();
      }
    },
    async confirmSaveBeforeNotifications() {
      if (
        this.notificationTabTransitioning ||
        this.saving ||
        !this.isDirty
      ) {
        return;
      }
      this.notificationTabTransitioning = true;
      try {
        const confirmation = await this.$fire({
          title: "Simpan perubahan form terlebih dahulu?",
          text:
            "Anda memiliki perubahan pada susunan form yang belum disimpan. Field WhatsApp yang baru ditambahkan harus tersimpan terlebih dahulu agar dapat digunakan sebagai penerima notifikasi.",
          type: "question",
          showCancelButton: true,
          confirmButtonText: "Simpan dan Lanjutkan",
          cancelButtonText: "Batal",
          confirmButtonColor: "#FE9B44"
        });
        if (!confirmation || !confirmation.value) {
          return;
        }
        if (this.saving) {
          return;
        }

        const saved = await this.saveDraft();
        if (!saved) {
          return;
        }
        // Beri kesempatan route watcher/fetch detail (khusus form baru)
        // selesai mereset state sebelum tab Notifikasi diaktifkan.
        await this.$nextTick();
        this.activateTab("notifications");
      } finally {
        this.notificationTabTransitioning = false;
      }
    },
    normalizeNotificationConfig(data) {
      const source = data && typeof data === "object" ? data : {};
      const owner =
        source.owner && typeof source.owner === "object" ? source.owner : {};
      const respondent =
        source.respondent && typeof source.respondent === "object"
          ? source.respondent
          : {};
      const allowedTypes = ["field", "owner", "respondent"];
      const notifications = Array.isArray(source.notifications)
        ? source.notifications.map(function(rule, index) {
            const data = rule && typeof rule === "object" ? rule : {};
            const recipientType =
              typeof data.recipient_type === "string" &&
              allowedTypes.indexOf(data.recipient_type) !== -1
                ? data.recipient_type
                : "field";
            return {
              _uid: createUid("notification"),
              id: typeof data.id === "string" ? data.id : "",
              enabled: data.enabled === true,
              recipient_type: recipientType,
              recipient_field:
                recipientType === "field" &&
                typeof data.recipient_field === "string"
                  ? data.recipient_field
                  : "",
              license_id:
                typeof data.license_id === "string" ? data.license_id : "",
              template:
                typeof data.template === "string" ? data.template : "",
              sort_order: Number(data.sort_order) || index + 1
            };
          })
        : [];

      // Kompatibilitas form lama: backend dapat mengembalikan owner/respondent
      // tanpa rule generik. Tampilkan sebagai rule sintetis agar tidak hilang
      // dari UI. Jangan menambahkannya jika rule generik sudah ada.
      if (!notifications.length) {
        if (owner.enabled === true || owner.license_id) {
          notifications.push({
            _uid: createUid("notification"),
            id: "legacy_owner",
            enabled: owner.enabled === true,
            recipient_type: "owner",
            recipient_field: "",
            license_id:
              typeof owner.license_id === "string" ? owner.license_id : "",
            template: 'Form "{{form_name}}" baru saja menerima respons baru.',
            sort_order: 1
          });
        }
        if (respondent.enabled === true || respondent.license_id) {
          notifications.push({
            _uid: createUid("notification"),
            id: "legacy_respondent",
            enabled: respondent.enabled === true,
            recipient_type: "respondent",
            recipient_field: "",
            license_id:
              typeof respondent.license_id === "string"
                ? respondent.license_id
                : "",
            template:
              'Terima kasih, respons Anda untuk form "{{form_name}}" telah diterima.',
            sort_order: notifications.length + 1
          });
        }
      }

      return {
        form_id: source.form_id || this.formId,
        owner: {
          enabled: owner.enabled === true,
          license_id:
            typeof owner.license_id === "string" ? owner.license_id : ""
        },
        respondent: {
          enabled: respondent.enabled === true,
          license_id:
            typeof respondent.license_id === "string"
              ? respondent.license_id
              : "",
          fields: Array.isArray(respondent.fields)
            ? respondent.fields.filter(function(key) {
                return typeof key === "string" && key;
              })
            : []
        },
        notifications: notifications,
        owner_recipient:
          typeof source.owner_recipient === "string"
            ? source.owner_recipient
            : "",
        owner_recipient_valid: source.owner_recipient_valid === true,
        updated_at: Number(source.updated_at) || 0
      };
    },
    async loadNotificationSettings() {
      if (!this.formId || this.notificationLoading) {
        return;
      }
      this.notificationLoading = true;
      this.notificationLoadError = false;
      this.notificationLoadErrorMessage = "";
      this.loadNotificationLicenses();
      try {
        const response = await this.$store.dispatch(
          "waajoForms/getWaajoFormNotificationConfig",
          { formId: this.formId }
        );
        const data = response && response.data ? response.data : null;
        if (!data) {
          this.notificationLoadError = true;
          this.notificationLoadErrorMessage =
            "Konfigurasi notifikasi tidak tersedia.";
          return;
        }
        this.notificationConfig = this.normalizeNotificationConfig(data);
        this.notificationSaved = cloneJson(this.notificationConfig);
        this.notificationsLoaded = true;
      } catch (error) {
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        this.notificationLoadError = true;
        this.notificationLoadErrorMessage = this.getErrorMessage(
          error,
          "Konfigurasi notifikasi belum dapat dimuat."
        );
      } finally {
        this.notificationLoading = false;
      }
    },
    async loadNotificationLicenses() {
      if (this.licensesLoading) {
        return;
      }
      this.licensesLoading = true;
      this.licensesError = "";
      try {
        const response = await this.$store.dispatch(
          "license/getLicenseList",
          {
            license_status: "active",
            page: 0,
            perPage: 100
          }
        );
        const payload = response && response.data ? response.data : {};
        const rows = Array.isArray(payload.data) ? payload.data : [];
        this.licenseOptions = rows.filter(function(row) {
          return (
            row &&
            row.id &&
            (!row.status_label || row.status_label === "Active")
          );
        });
      } catch (error) {
        this.licensesError = this.getErrorMessage(
          error,
          "Daftar nomor WhatsApp pengirim belum dapat dimuat."
        );
      } finally {
        this.licensesLoading = false;
      }
    },
    licenseOptionLabel(license) {
      const name = license && license.license_name ? license.license_name : "Nomor WhatsApp";
      const phone = license && license.license_phone_number
        ? this.maskPhone(license.license_phone_number)
        : "";
      return phone ? name + " - " + phone : name;
    },
    maskPhone(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (!digits) {
        return "";
      }
      if (digits.length <= 4) {
        return digits;
      }
      return digits.slice(0, 4) + "••••" + digits.slice(-4);
    },
    notificationRecipientLabel(type) {
      if (type === "owner") {
        return "Owner Form";
      }
      if (type === "field") {
        return "Field WhatsApp";
      }
      if (type === "respondent") {
        return "Pengisi form (legacy)";
      }
      return "Field WhatsApp";
    },
    notificationUsesField(type) {
      return type === "field";
    },
    defaultOwnerNotificationTemplate() {
      return 'Form "{{form_name}}" baru saja menerima respons baru.';
    },
    defaultFieldNotificationTemplate() {
      const intro =
        'Terimakasih telah mengisi Form "{{form_name}}", berikut data yang telah anda isi:';
      const seen = {};
      const lines = [];
      this.schemaNotificationFields.forEach(function(field) {
        const key = String(field.key || "").trim();
        if (!key || key === "form_name" || seen[key]) {
          return;
        }
        seen[key] = true;
        const label =
          String(field.label || key)
            .replace(/[{}]/g, "")
            .trim() || key;
        lines.push(label + ": {{" + key + "}}");
      });
      return lines.length ? intro + "\n\n" + lines.join("\n") : intro;
    },
    defaultNotificationTemplate(rule) {
      if (rule && rule.recipient_type === "field") {
        return this.defaultFieldNotificationTemplate();
      }
      return this.defaultOwnerNotificationTemplate();
    },
    onNotificationRecipientTypeChange(rule) {
      if (!rule) {
        return;
      }
      const ownerDefault = this.defaultOwnerNotificationTemplate();
      const fieldDefault = this.defaultFieldNotificationTemplate();
      const current = String(rule.template || "").trim();
      if (rule.recipient_type === "field") {
        if (!current || rule.template === ownerDefault) {
          rule.template = fieldDefault;
        }
      } else if (rule.recipient_type === "owner") {
        if (!current || rule.template === fieldDefault) {
          rule.template = ownerDefault;
        }
      }
      if (rule.recipient_type !== "field") {
        rule.recipient_field = "";
      }
    },
    async restoreNotificationDefaultTemplate(rule) {
      if (!rule || !rule.enabled || this.notificationSaving) {
        return;
      }
      const nextTemplate = this.defaultNotificationTemplate(rule);
      const current = String(rule.template || "").trim();
      if (current && rule.template !== nextTemplate) {
        const confirmation = await this.$fire({
          title: "Buat ulang template default?",
          text:
            "Template yang sekarang sudah diedit akan diganti dengan template default terbaru.",
          type: "question",
          showCancelButton: true,
          confirmButtonText: "Ya, buat ulang",
          cancelButtonText: "Batal",
          confirmButtonColor: "#FE9B44"
        });
        if (!confirmation || !confirmation.value) {
          return;
        }
      }
      rule.template = nextTemplate;
    },
    applyNotificationFormat(rule, marker) {
      if (!rule || !marker || !rule.enabled || this.notificationSaving) {
        return;
      }
      const element = document.getElementById(
        this.notificationTemplateElementId(rule)
      );
      const current = String(rule.template || "");
      let start = current.length;
      let end = current.length;
      if (
        element &&
        typeof element.selectionStart === "number" &&
        typeof element.selectionEnd === "number"
      ) {
        start = element.selectionStart;
        end = element.selectionEnd;
      }
      const selected = current.slice(start, end);
      const replacement = marker + selected + marker;
      rule.template = current.slice(0, start) + replacement + current.slice(end);
      this.$nextTick(() => {
        const target = document.getElementById(
          this.notificationTemplateElementId(rule)
        );
        if (!target) {
          return;
        }
        target.focus();
        if (target.setSelectionRange) {
          if (selected) {
            target.setSelectionRange(
              start + marker.length,
              start + marker.length + selected.length
            );
          } else {
            const position = start + marker.length;
            target.setSelectionRange(position, position);
          }
        }
      });
    },
    notificationOwnerRecipientLabel() {
      if (!this.notificationConfig.owner_recipient_valid) {
        return "Nomor WhatsApp owner belum valid. Perbarui nomor pada profil member.";
      }
      return (
        "Penerima: owner form (" +
        this.maskPhone(this.notificationConfig.owner_recipient) +
        ")"
      );
    },
    notificationTemplateElementId(rule) {
      return "wf-notification-template-" + (rule._uid || rule.id || "rule");
    },
    insertNotificationPlaceholder(rule, fieldKey) {
      if (!rule || !fieldKey) {
        return;
      }
      const token = "{{" + fieldKey + "}}";
      const element = document.getElementById(
        this.notificationTemplateElementId(rule)
      );
      const current = String(rule.template || "");
      let start = current.length;
      let end = current.length;
      if (
        element &&
        typeof element.selectionStart === "number" &&
        typeof element.selectionEnd === "number"
      ) {
        start = element.selectionStart;
        end = element.selectionEnd;
      }
      rule.template = current.slice(0, start) + token + current.slice(end);
      this.$nextTick(() => {
        const target = document.getElementById(
          this.notificationTemplateElementId(rule)
        );
        if (target) {
          target.focus();
          const position = start + token.length;
          if (target.setSelectionRange) {
            target.setSelectionRange(position, position);
          }
        }
      });
    },
    addNotification() {
      if (this.notificationConfig.notifications.length >= MAX_NOTIFICATIONS) {
        return;
      }
      if (
        !this.respondentWhatsAppFields.length &&
        this.notificationOwnerRuleCount > 0
      ) {
        return;
      }
      // Default konfigurasi baru adalah owner. Jika owner sudah ada, gunakan
      // field WhatsApp agar tidak membuat konfigurasi owner duplikat.
      const recipientType =
        this.notificationOwnerRuleCount > 0 ? "field" : "owner";
      const template =
        recipientType === "field"
          ? this.defaultFieldNotificationTemplate()
          : this.defaultOwnerNotificationTemplate();
      this.notificationConfig.notifications.push({
        _uid: createUid("notification"),
        id: "",
        enabled: true,
        recipient_type: recipientType,
        recipient_field: "",
        license_id: "",
        template: template,
        sort_order: this.notificationConfig.notifications.length + 1
      });
    },
    removeNotification(index) {
      if (this.notificationSaving) {
        return;
      }
      this.notificationConfig.notifications.splice(index, 1);
      this.notificationConfig.notifications.forEach((rule, ruleIndex) => {
        rule.sort_order = ruleIndex + 1;
      });
    },
    notificationPreviewFieldExample(field) {
      if (!field) {
        return "";
      }
      const label = field.label || field.key || "Field";
      if (field.type === "whatsapp") {
        return "0812xxxxxxx";
      }
      if (field.type === "email") {
        return "nama@email.com";
      }
      if (field.type === "number") {
        return "123";
      }
      if (field.type === "date") {
        return "2026-01-01";
      }
      if (
        field.type === "radio" ||
        field.type === "dropdown" ||
        field.type === "checkbox"
      ) {
        const option = Array.isArray(field.options) ? field.options[0] : null;
        return option && option.label ? option.label : "[" + label + "]";
      }
      return "[" + label + "]";
    },
    notificationRulePreview(rule) {
      if (!rule) {
        return "";
      }
      return String(rule.template || "").replace(
        /\{\{\s*([a-zA-Z][a-zA-Z0-9_]{0,49})\s*\}\}/g,
        (match, key) => {
          if (key === "form_name") {
            return this.formName || "Form Tanpa Nama";
          }
          const field = this.schemaNotificationFields.find(
            item => item.key === key
          );
          return field
            ? this.notificationPreviewFieldExample(field)
            : "{{" + key + "}}";
        }
      );
    },
    escapeNotificationHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    renderWhatsAppFormatting(text) {
      const markers = [
        {
          token: "```",
          open:
            '<code style="font-family:Consolas,monospace;font-size:0.95em;background:rgba(15,23,42,0.08);padding:1px 3px;border-radius:3px;">',
          close: "</code>"
        },
        { token: "*", open: '<strong style="font-weight:700;">', close: "</strong>" },
        { token: "_", open: '<em style="font-style:italic;">', close: "</em>" },
        {
          token: "~",
          open: '<s style="text-decoration:line-through;">',
          close: "</s>"
        }
      ];
      let result = "";
      let cursor = 0;
      while (cursor < text.length) {
        let next = null;
        for (let index = 0; index < markers.length; index += 1) {
          const marker = markers[index];
          const position = text.indexOf(marker.token, cursor);
          if (position === -1) {
            continue;
          }
          if (
            !next ||
            position < next.position ||
            (position === next.position &&
              marker.token.length > next.marker.token.length)
          ) {
            next = { marker: marker, position: position };
          }
        }
        if (!next) {
          result += text.slice(cursor);
          break;
        }
        result += text.slice(cursor, next.position);
        const innerStart = next.position + next.marker.token.length;
        const closePosition = text.indexOf(next.marker.token, innerStart);
        if (closePosition === -1) {
          result += text.slice(next.position, innerStart);
          cursor = innerStart;
          continue;
        }
        const inner = text.slice(innerStart, closePosition);
        if (!inner) {
          result += text.slice(
            next.position,
            closePosition + next.marker.token.length
          );
          cursor = closePosition + next.marker.token.length;
          continue;
        }
        result +=
          next.marker.open +
          this.renderWhatsAppFormatting(inner) +
          next.marker.close;
        cursor = closePosition + next.marker.token.length;
      }
      return result;
    },
    notificationPreviewHtml(rule) {
      const rendered = this.notificationRulePreview(rule);
      const escaped = this.escapeNotificationHtml(rendered);
      return this.renderWhatsAppFormatting(escaped)
        .replace(/\r\n/g, "\n")
        .replace(/\n/g, "<br />");
    },
    validateNotificationSettings() {
      const rules = this.notificationConfig.notifications;
      if (rules.length > MAX_NOTIFICATIONS) {
        return "Maksimal 3 konfigurasi notifikasi per form.";
      }
      const ownerRules = rules.filter(function(rule) {
        return rule && rule.recipient_type === "owner";
      });
      if (ownerRules.length > 1) {
        return "Hanya boleh ada satu konfigurasi notifikasi owner.";
      }
      const allowedTypes = ["field", "owner", "respondent"];
      for (let index = 0; index < rules.length; index += 1) {
        const rule = rules[index];
        if (allowedTypes.indexOf(rule.recipient_type) === -1) {
          return "Jenis penerima notifikasi " + (index + 1) + " tidak valid.";
        }
        if (!rule.enabled) {
          continue;
        }
        if (!rule.license_id) {
          return "Pilih nomor WhatsApp pengirim untuk notifikasi " + (index + 1) + ".";
        }
        if (
          !this.licenseOptions.some(license => license.id === rule.license_id)
        ) {
          return (
            "Nomor pengirim notifikasi " +
            (index + 1) +
            " tidak lagi tersedia. Pilih ulang nomor pengirim."
          );
        }
        if (rule.recipient_type === "owner") {
          if (!this.notificationConfig.owner_recipient_valid) {
            return "Nomor WhatsApp owner belum valid. Perbarui nomor pada profil member terlebih dahulu.";
          }
        } else if (rule.recipient_type === "field") {
          if (!rule.recipient_field) {
            return "Pilih field nomor WhatsApp penerima untuk notifikasi " + (index + 1) + ".";
          }
          if (!this.respondentWhatsAppFields.some(field => field.key === rule.recipient_field)) {
            return "Field penerima notifikasi " + (index + 1) + " tidak valid.";
          }
        }
        if (!String(rule.template || "").trim()) {
          return "Template notifikasi " + (index + 1) + " wajib diisi.";
        }
        if (String(rule.template || "").length > MAX_NOTIFICATION_TEMPLATE) {
          return "Template notifikasi maksimal 5000 karakter.";
        }
      }
      return "";
    },
    removeMissingNotificationFields() {
      this.notificationConfig.respondent.fields = this.notificationConfig.respondent.fields.filter(
        key => this.missingNotificationFields.indexOf(key) === -1
      );
    },
    async saveNotificationSettings() {
      if (this.notificationSaving || !this.formId) {
        return;
      }
      const validationMessage = this.validateNotificationSettings();
      if (validationMessage) {
        this.notificationError = validationMessage;
        this.notificationSuccess = "";
        return;
      }
      this.notificationSaving = true;
      this.notificationError = "";
      this.notificationSuccess = "";
      try {
        const rules = this.notificationConfig.notifications.map(
          (rule, index) => {
            const recipientType =
              rule.recipient_type === "owner" ||
              rule.recipient_type === "respondent"
                ? rule.recipient_type
                : "field";
            return {
              id: rule.id || "",
              enabled: rule.enabled === true,
              recipient_type: recipientType,
              recipient_field:
                recipientType === "field" ? rule.recipient_field || "" : "",
              license_id: rule.license_id || "",
              template: rule.template || "",
              sort_order: index + 1
            };
          }
        );
        const response = await this.$store.dispatch(
          "waajoForms/updateWaajoFormNotificationConfig",
          {
            form_id: this.formId,
            notifications: rules
          }
        );
        const data = response && response.data ? response.data : null;
        if (data) {
          this.notificationConfig = this.normalizeNotificationConfig(data);
        }
        this.notificationSaved = cloneJson(this.notificationConfig);
        this.notificationSuccess = "Pengaturan notifikasi berhasil disimpan.";
      } catch (error) {
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        this.notificationError = this.getErrorMessage(
          error,
          "Pengaturan notifikasi belum dapat disimpan."
        );
      } finally {
        this.notificationSaving = false;
      }
    },
    cancelNotificationChanges() {
      if (this.notificationSaving) {
        return;
      }
      this.notificationConfig = cloneJson(this.notificationSaved);
      this.notificationError = "";
      this.notificationSuccess = "";
    },
    chooseImage() {
      if (!this.formId) {
        this.imageUploadError =
          "Simpan form sebagai draft terlebih dahulu sebelum mengunggah gambar.";
        return;
      }
      if (this.uploadingImage) {
        return;
      }
      if (this.$refs.imageInput) {
        this.$refs.imageInput.click();
      }
    },
    validateImageFile(file) {
      if (!file) {
        return "Pilih file gambar terlebih dahulu.";
      }
      if (file.size > MAX_IMAGE_BYTES) {
        return "Ukuran gambar maksimal 2 MB.";
      }
      const type = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();
      const extensionAllowed = IMAGE_EXTENSIONS.some(function(extension) {
        return name.slice(-extension.length) === extension;
      });
      if (IMAGE_TYPES.indexOf(type) === -1 && !extensionAllowed) {
        return "Format gambar harus JPG, PNG, WebP, atau GIF.";
      }
      return "";
    },
    onImageSelected(event) {
      const input = event && event.target ? event.target : null;
      const file = input && input.files && input.files[0] ? input.files[0] : null;
      if (input) {
        input.value = "";
      }
      if (!file) {
        return;
      }
      this.imageUploadError = "";
      this.imageUploadSuccess = "";
      this.imageSelectedName = file.name || "";
      this.imageSelectedSize = Number(file.size) || 0;
      const validationMessage = this.validateImageFile(file);
      if (validationMessage) {
        this.imageUploadError = validationMessage;
        return;
      }
      this.uploadImage(file);
    },
    async uploadImage(file) {
      if (this.uploadingImage || !this.formId) {
        return;
      }
      this.uploadingImage = true;
      this.imageUploadError = "";
      this.imageUploadSuccess = "";
      try {
        const response = await this.$store.dispatch(
          "waajoForms/uploadWaajoFormImage",
          { formId: this.formId, file: file }
        );
        const data = response && response.data ? response.data : null;
        if (!data || !data.image_url) {
          throw new Error("Gambar tidak tersedia pada response.");
        }
        this.formImageUrl = data.image_url;
        this.imagePreviewError = false;
        this.imageUploadSuccess = "Gambar form berhasil diperbarui.";
      } catch (error) {
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        if (getHttpStatus(error) === 413) {
          this.imageUploadError = "Ukuran gambar maksimal 2 MB.";
        } else {
          this.imageUploadError =
            getHttpStatus(error) === 406
              ? extractBackendMessage(error) ||
                "Gambar belum dapat diunggah. Periksa format dan ukuran file."
              : this.getErrorMessage(
                  error,
                  "Gambar belum dapat diunggah. Coba lagi."
                );
        }
      } finally {
        this.uploadingImage = false;
      }
    },
    formatFileSize(bytes) {
      const size = Number(bytes) || 0;
      if (size >= 1024 * 1024) {
        return (size / (1024 * 1024)).toFixed(1) + " MB";
      }
      return Math.max(1, Math.round(size / 1024)) + " KB";
    },
    setupCreateForm() {
      const page = createPage([], "Halaman 1");
      const initialField = createField("text", [], {
        label: "Nama Lengkap",
        key: "nama_lengkap",
        required: true,
        placeholder: "Masukkan nama lengkap",
        page_id: page.id
      });
      this.formStatus = "draft";
      this.schemaVersion = 0;
      this.pages = [page];
      this.activePageId = page.id;
      this.fields = [initialField];
      this.selectedUid = initialField._uid;
      this.ensurePreviewValue(initialField);
      this.savedSnapshot = this.buildSnapshot();
      this.loading = false;
    },
    async fetchFormDetail() {
      const id = this.$route && this.$route.params ? this.$route.params.id : "";
      if (!id) {
        this.loading = false;
        this.loadError = true;
        this.loadErrorMessage = "ID form tidak ditemukan pada URL.";
        return;
      }

      this.loading = true;
      this.loadError = false;
      this.loadErrorMessage = "";
      try {
        const response = await this.$store.dispatch(
          "waajoForms/detailWaajoForm",
          { id: id }
        );
        const data = response && response.data ? response.data : null;
        if (!data) {
          this.loading = false;
          this.loadError = true;
          this.loadErrorMessage =
            "Detail form tidak tersedia. Muat ulang halaman atau kembali ke daftar form.";
          return;
        }

        this.formId = data.id || id;
        this.formName = typeof data.name === "string" ? data.name : "";
        this.formDescription =
          typeof data.description === "string" ? data.description : "";
        this.formStatus =
          typeof data.status === "string" ? data.status : "draft";
        this.schemaVersion = Number(data.schema_version) || 0;
        this.updateDatetime = Number(data.update_datetime) || 0;
        this.formImageUrl =
          typeof data.image_url === "string" ? data.image_url : "";
        this.imagePreviewError = false;
        this.fields = Array.isArray(data.fields)
          ? data.fields.map(normalizeFieldFromServer)
          : [];
        this.pages = normalizePagesFromServer(data.pages, this.fields);
        this.activePageId = this.pages.length ? this.pages[0].id : "";
        this.selectedUid = this.fields.length ? this.fields[0]._uid : "";
        this.ensureAllPreviewValues();
        this.savedSnapshot = this.buildSnapshot();
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.loadError = true;
        this.loadErrorMessage = this.getErrorMessage(
          error,
          "Detail form belum dapat dimuat. Coba lagi."
        );
      }
    },
    buildSnapshot() {
      return JSON.stringify(
        buildFormPayload({
          id: this.formId,
          name: this.formName,
          description: this.formDescription,
          fields: this.fields,
          pages: this.pages,
          includeFields: !this.isSchemaLocked
        })
      );
    },
    typeMeta(type) {
      return getFieldTypeMeta(type);
    },
    paletteItemTitle(item) {
      if (this.isSchemaLocked) {
        return "Schema form aktif terkunci.";
      }
      if (this.isPreviewMode) {
        return "Kembali ke mode desain untuk menambah field.";
      }
      if (this.fields.length >= MAX_FIELDS) {
        return "Maksimal 30 field per form.";
      }
      return "Klik atau seret untuk menambahkan " + item.label;
    },
    notifyCannotAddField() {
      let message = "Field belum dapat ditambahkan.";
      if (this.isSchemaLocked) {
        message =
          "Form aktif harus dinonaktifkan terlebih dahulu sebelum schema dapat diubah.";
      } else if (this.isPreviewMode) {
        message = "Kembali ke mode desain untuk menambah field.";
      } else if (this.fields.length >= MAX_FIELDS) {
        message = "Maksimal 30 field dalam satu form.";
      }
      this.$fire({
        title: "Tidak dapat menambah field",
        text: message,
        type: "info",
        timer: 3000
      });
    },
    addField(type) {
      if (!this.canAddField) {
        this.notifyCannotAddField();
        return;
      }
      const field = createField(type, this.fields, {
        page_id: this.activePageId || (this.pages[0] && this.pages[0].id) || ""
      });
      this.fields.push(field);
      this.selectedUid = field._uid;
      this.ensurePreviewValue(field);
      this.scrollToField(field._uid);
    },
    insertFieldAt(type, index) {
      if (!this.canAddField) {
        this.notifyCannotAddField();
        return;
      }
      const pageFields = this.currentPageFields;
      let target = this.fields.length;
      if (pageFields.length) {
        if (index >= pageFields.length) {
          const last = pageFields[pageFields.length - 1];
          target = this.fields.findIndex(field => field._uid === last._uid) + 1;
        } else {
          target = this.fields.findIndex(
            field => field._uid === pageFields[index]._uid
          );
        }
      }
      if (target < 0) {
        target = this.fields.length;
      }
      const field = createField(type, this.fields, {
        page_id: this.activePageId || (this.pages[0] && this.pages[0].id) || ""
      });
      this.fields.splice(target, 0, field);
      this.selectedUid = field._uid;
      this.ensurePreviewValue(field);
      this.scrollToField(field._uid);
    },
    setActivePage(pageId) {
      if (!pageId || pageId === this.activePageId) {
        return;
      }
      if (!this.pages.some(page => page.id === pageId)) {
        return;
      }
      this.activePageId = pageId;
      const first = this.currentPageFields[0];
      this.selectedUid = first ? first._uid : "";
    },
    addPage() {
      if (!this.canAddPage) {
        this.$fire({
          title: "Batas halaman tercapai",
          text: "Maksimal 5 halaman per form.",
          type: "info",
          timer: 3000
        });
        return;
      }
      const page = createPage(this.pages);
      this.pages.push(page);
      this.activePageId = page.id;
      this.selectedUid = "";
    },
    removePage(page) {
      if (!this.canModifyFields || !page) {
        return;
      }
      if (this.pages.length <= 1) {
        this.$fire({
          title: "Halaman tidak dapat dihapus",
          text: "Form minimal memiliki satu halaman.",
          type: "info",
          timer: 3000
        });
        return;
      }
      const pageFields = this.fields.filter(field => field.page_id === page.id);
      const fallback = this.pages.find(item => item.id !== page.id);
      const proceed = () => {
        this.fields.forEach(field => {
          if (field.page_id === page.id) {
            field.page_id = fallback.id;
          }
        });
        this.pages = this.pages.filter(item => item.id !== page.id);
        this.pages.forEach((item, index) => {
          item.sort_order = index + 1;
        });
        this.activePageId = fallback.id;
        this.selectedUid = this.currentPageFields.length
          ? this.currentPageFields[0]._uid
          : "";
      };
      if (!pageFields.length) {
        proceed();
        return;
      }
      this.$fire({
        title: "Hapus halaman?",
        text:
          "Halaman " +
          (page.title || "") +
          " memiliki " +
          pageFields.length +
          " field. Field akan dipindahkan ke halaman lain.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Hapus",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      }).then(result => {
        if (result && result.value) {
          proceed();
        }
      });
    },
    movePageStep(page, direction) {
      if (!this.canModifyFields || !page) {
        return;
      }
      const index = this.pages.findIndex(item => item.id === page.id);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= this.pages.length) {
        return;
      }
      this.pages.splice(index, 1);
      this.pages.splice(target, 0, page);
      this.pages.forEach((item, pageIndex) => {
        item.sort_order = pageIndex + 1;
      });
    },
    movePageSelection(direction) {
      const target = this.currentPageIndex + direction;
      if (target < 0 || target >= this.pages.length) {
        return;
      }
      this.setActivePage(this.pages[target].id);
    },
    pageFieldCount(page) {
      return this.fields.filter(field => field.page_id === page.id).length;
    },
    selectField(field) {
      if (!field || this.isPreviewMode) {
        return;
      }
      this.selectedUid = field._uid;
    },
    changeSelectedFieldType(type) {
      const field = this.selectedField;
      if (!this.canModifyFields || !field || field.type === type) {
        return;
      }

      const previousMeta = getFieldTypeMeta(field.type);
      const nextMeta = getFieldTypeMeta(type);
      field.type = nextMeta.type;

      if ("placeholder" in field) {
        if (
          !field.placeholder ||
          field.placeholder === previousMeta.defaultPlaceholder
        ) {
          field.placeholder = nextMeta.defaultPlaceholder || "";
        }
      }

      if (isChoiceType(field.type)) {
        field.placeholder = "";
        if (!Array.isArray(field.options) || field.options.length === 0) {
          field.options = createDefaultOptions();
        } else if (field.options.length > MAX_OPTIONS) {
          field.options = field.options.slice(0, MAX_OPTIONS);
        }
      } else {
        field.options = [];
      }

      this.resetPreviewValue(field);
    },
    regenerateSelectedKey() {
      const field = this.selectedField;
      if (!this.canModifyFields || !field) {
        return;
      }
      field.key = makeUniqueKey(field.label, this.fields, field._uid);
    },
    addOption(field) {
      if (!this.canModifyFields || !field || !isChoiceType(field.type)) {
        return;
      }
      if (!Array.isArray(field.options)) {
        field.options = [];
      }
      if (field.options.length >= MAX_OPTIONS) {
        return;
      }
      const number = field.options.length + 1;
      const label = "Opsi " + number;
      const value = makeUniqueOptionValue("opsi_" + number, field.options);
      field.options.push(createOption(label, value));
    },
    removeOption(field, option) {
      if (
        !this.canModifyFields ||
        !field ||
        !Array.isArray(field.options) ||
        field.options.length <= 1
      ) {
        return;
      }
      const index = field.options.findIndex(item => item._uid === option._uid);
      if (index === -1) {
        return;
      }
      const removed = field.options[index];
      field.options.splice(index, 1);

      if (removed && Array.isArray(this.previewValues[field._uid])) {
        this.$set(
          this.previewValues,
          field._uid,
          this.previewValues[field._uid].filter(
            value => value !== removed.value
          )
        );
      } else if (removed && this.previewValues[field._uid] === removed.value) {
        this.$set(this.previewValues, field._uid, "");
      }
    },
    moveFieldStep(field, direction) {
      if (!this.canModifyFields || !field) {
        return;
      }
      const pageFields = this.currentPageFields;
      const pageIndex = pageFields.findIndex(item => item._uid === field._uid);
      const targetPageIndex = pageIndex + direction;
      if (
        pageIndex === -1 ||
        targetPageIndex < 0 ||
        targetPageIndex >= pageFields.length
      ) {
        return;
      }
      const index = this.fields.findIndex(item => item._uid === field._uid);
      const other = pageFields[targetPageIndex];
      const otherIndex = this.fields.findIndex(item => item._uid === other._uid);
      this.fields.splice(index, 1, other);
      this.fields.splice(otherIndex, 1, field);
      this.dropIndex = -1;
    },
    moveFieldByUid(uid, insertionIndex) {
      const field = this.fields.find(item => item._uid === uid);
      if (!field) {
        return;
      }
      if (field.page_id !== this.activePageId) {
        field.page_id = this.activePageId;
      }
      const pageFields = this.currentPageFields.filter(item => item._uid !== uid);
      let localIndex = Math.min(
        Math.max(Number(insertionIndex) || 0, 0),
        pageFields.length
      );
      const fromIndex = this.fields.findIndex(item => item._uid === uid);
      this.fields.splice(fromIndex, 1);

      let target = this.fields.length;
      if (pageFields.length) {
        if (localIndex >= pageFields.length) {
          const last = pageFields[pageFields.length - 1];
          target = this.fields.findIndex(item => item._uid === last._uid) + 1;
        } else {
          target = this.fields.findIndex(
            item => item._uid === pageFields[localIndex]._uid
          );
        }
      }
      if (target < 0) {
        target = this.fields.length;
      }
      this.fields.splice(target, 0, field);
    },
    duplicateField(field) {
      if (!this.canModifyFields || !field) {
        return;
      }
      const index = this.fields.findIndex(item => item._uid === field._uid);
      if (index === -1) {
        return;
      }
      const copy = {
        _uid: createUid("field"),
        key: makeUniqueKey(field.key, this.fields),
        type: field.type,
        label: (field.label
          ? field.label + " (Salinan)"
          : "Field (Salinan)"
        ).slice(0, 150),
        required: field.required === true,
        placeholder: field.placeholder || "",
        options: [],
        page_id: field.page_id || this.activePageId
      };
      if (isChoiceType(field.type)) {
        copy.options = (Array.isArray(field.options) ? field.options : []).map(
          option => ({
            _uid: createUid("opt"),
            value: option.value,
            label: option.label
          })
        );
      }
      this.fields.splice(index + 1, 0, copy);
      this.selectedUid = copy._uid;
      this.ensurePreviewValue(copy);
      this.scrollToField(copy._uid);
    },
    deleteField(field) {
      if (!this.canModifyFields || !field) {
        return;
      }
      const index = this.fields.findIndex(item => item._uid === field._uid);
      if (index === -1) {
        return;
      }
      this.fields.splice(index, 1);
      this.$delete(this.previewValues, field._uid);

      if (this.selectedUid === field._uid) {
        if (this.currentPageFields.length) {
          const nextIndex = Math.min(
            index,
            this.currentPageFields.length - 1
          );
          this.selectedUid = this.currentPageFields[nextIndex]._uid;
        } else if (this.fields.length) {
          this.selectedUid = this.fields[0]._uid;
        } else {
          this.selectedUid = "";
        }
      }
      this.dropIndex = -1;
    },
    scrollToField(uid) {
      this.$nextTick(() => {
        const element = document.querySelector(
          '[data-field-uid="' + uid + '"]'
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    ensurePreviewValue(field) {
      if (!field) {
        return;
      }
      if (field.type === "checkbox") {
        if (!Array.isArray(this.previewValues[field._uid])) {
          this.$set(this.previewValues, field._uid, []);
        }
      } else if (
        this.previewValues[field._uid] === undefined ||
        this.previewValues[field._uid] === null
      ) {
        this.$set(this.previewValues, field._uid, "");
      }
    },
    ensureAllPreviewValues() {
      this.fields.forEach(field => this.ensurePreviewValue(field));
    },
    resetPreviewValue(field) {
      if (!field) {
        return;
      }
      this.$set(
        this.previewValues,
        field._uid,
        field.type === "checkbox" ? [] : ""
      );
    },
    onPreviewInput(field, value) {
      if (!field) {
        return;
      }
      this.$set(this.previewValues, field._uid, value);
    },
    togglePreview() {
      if (this.loading || this.loadError || this.isLifecycleBusy) {
        return;
      }
      this.onDragEnd();
      this.canvasMode = this.isPreviewMode ? "design" : "preview";
      if (this.isPreviewMode) {
        this.ensureAllPreviewValues();
      }
    },
    onPaletteDragStart(event, type) {
      if (!this.canAddField) {
        if (event && event.preventDefault) {
          event.preventDefault();
        }
        return;
      }
      this.dragState = {
        active: true,
        kind: "palette",
        type: type,
        fieldUid: ""
      };
      this.dropIndex = this.currentPageFields.length;
      if (event && event.dataTransfer) {
        try {
          event.dataTransfer.effectAllowed = "copy";
          event.dataTransfer.setData("text/plain", "palette:" + type);
        } catch (error) {
          // DataTransfer tidak tersedia pada sebagian browser lama.
        }
      }
    },
    onFieldDragStart(event, field, index) {
      if (!this.canModifyFields || !field) {
        if (event && event.preventDefault) {
          event.preventDefault();
        }
        return;
      }
      this.selectedUid = field._uid;
      this.dragState = {
        active: true,
        kind: "field",
        type: field.type,
        fieldUid: field._uid
      };
      this.dropIndex = index;
      if (event && event.dataTransfer) {
        try {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", "field:" + field._uid);
        } catch (error) {
          // DataTransfer tidak tersedia pada sebagian browser lama.
        }
      }
    },
    onDragEnd() {
      this.dragState = {
        active: false,
        kind: "",
        type: "",
        fieldUid: ""
      };
      this.dropIndex = -1;
    },
    onCanvasDragOver(event) {
      if (!this.dragState.active || !this.canModifyFields) {
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect =
          this.dragState.kind === "palette" ? "copy" : "move";
      }
      if (!this.currentPageFields.length) {
        this.dropIndex = 0;
      }
    },
    onEmptyCanvasDragOver(event) {
      if (!this.dragState.active || !this.canModifyFields) {
        return;
      }
      event.preventDefault();
      this.dropIndex = 0;
    },
    onFieldDragOver(event, index) {
      if (!this.dragState.active || !this.canModifyFields) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      this.dropIndex = event.clientY > middle ? index + 1 : index;
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect =
          this.dragState.kind === "palette" ? "copy" : "move";
      }
    },
    onTailDragOver(event) {
      if (!this.dragState.active || !this.canModifyFields) {
        return;
      }
      event.preventDefault();
      this.dropIndex = this.currentPageFields.length;
    },
    commitDragDrop(index) {
      if (!this.dragState.active) {
        return;
      }
      const state = {
        kind: this.dragState.kind,
        type: this.dragState.type,
        fieldUid: this.dragState.fieldUid
      };
      this.onDragEnd();

      if (state.kind === "palette") {
        this.insertFieldAt(state.type, index);
      } else if (state.kind === "field") {
        this.moveFieldByUid(state.fieldUid, index);
      }
    },
    onCanvasDrop(event) {
      if (!this.dragState.active) {
        return;
      }
      event.preventDefault();
      const index =
        this.dropIndex >= 0 ? this.dropIndex : this.currentPageFields.length;
      this.commitDragDrop(index);
    },
    onFieldDrop(event, index) {
      if (!this.dragState.active) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      const target = event.clientY > middle ? index + 1 : index;
      this.commitDragDrop(target);
    },
    onTailDrop(event) {
      if (!this.dragState.active) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.commitDragDrop(this.currentPageFields.length);
    },
    onEmptyDrop(event) {
      if (!this.dragState.active) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.commitDragDrop(0);
    },
    fieldErrorSummary(field) {
      if (!field) {
        return "";
      }
      const fieldError = this.validation.fields[field._uid];
      if (!fieldError) {
        return "";
      }
      if (fieldError.label) {
        return fieldError.label;
      }
      if (fieldError.key) {
        return fieldError.key;
      }
      if (fieldError.placeholder) {
        return fieldError.placeholder;
      }
      if (fieldError.options) {
        return fieldError.options;
      }
      const optionUids = Object.keys(fieldError.optionErrors || {});
      for (let index = 0; index < optionUids.length; index += 1) {
        const optionError = fieldError.optionErrors[optionUids[index]];
        if (optionError && optionError.label) {
          return optionError.label;
        }
        if (optionError && optionError.value) {
          return optionError.value;
        }
      }
      return "";
    },
    optionError(option, key) {
      if (!option || !this.selectedFieldErrors.optionErrors) {
        return "";
      }
      const error = this.selectedFieldErrors.optionErrors[option._uid];
      return error ? error[key] || "" : "";
    },
    fieldHasAnyError(uid) {
      const fieldError = this.validation.fields[uid];
      if (!fieldError) {
        return false;
      }
      if (
        fieldError.label ||
        fieldError.key ||
        fieldError.placeholder ||
        fieldError.options
      ) {
        return true;
      }
      return Object.keys(fieldError.optionErrors || {}).some(optionUid => {
        const optionError = fieldError.optionErrors[optionUid];
        return !!(optionError && (optionError.value || optionError.label));
      });
    },
    focusFirstError(errors) {
      if (errors.name) {
        this.$nextTick(() => {
          const input = this.$refs.formNameInput;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
        return;
      }

      const invalidUid = Object.keys(errors.fields || {}).find(uid =>
        this.fieldHasAnyError(uid)
      );
      if (invalidUid) {
        this.selectedUid = invalidUid;
        this.$nextTick(() => {
          const input = this.$refs.fieldLabelInput;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          const element = document.querySelector(
            '[data-field-uid="' + invalidUid + '"]'
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
        return;
      }

      if (errors.description || errors.general) {
        this.$nextTick(() => {
          const element = document.querySelector(".wf-canvas-form");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }
    },
    async copyCurrentPublicLink() {
      if (this.formStatus !== "active" || !this.formId) {
        return;
      }
      const url = getPublicFormUrl(this.formId);
      try {
        const copied = await copyTextToClipboard(url);
        if (!copied) {
          throw new Error("copy failed");
        }
        this.$fire({
          title: "Link publik disalin",
          text:
            url +
            "\n\nHalaman publik akan tersedia pada tahap berikutnya.",
          type: "success",
          timer: 3200
        });
      } catch (error) {
        this.$fire({
          title: "Gagal menyalin link",
          text: "Salin link berikut secara manual: " + url,
          type: "warning"
        });
      }
    },
    async promptSaveBeforeLifecycle(action) {
      const isPublish = action === "publish";
      const result = await this.$fire({
        title: isPublish
          ? "Simpan draft terlebih dahulu"
          : "Simpan perubahan terlebih dahulu",
        text: isPublish
          ? "Publish selalu menggunakan schema yang sudah tersimpan di server. Simpan draft terlebih dahulu, lalu ulangi aksi publikasikan."
          : "Masih ada perubahan yang belum disimpan. Simpan perubahan terlebih dahulu agar perubahan tidak hilang saat form dinonaktifkan.",
        type: "info",
        showCancelButton: true,
        confirmButtonText: isPublish ? "Simpan Draft" : "Simpan Perubahan",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      });
      if (result && result.value) {
        await this.saveDraft();
      }
    },
    async publishForm() {
      if (
        this.lifecycleAction ||
        this.saving ||
        this.loading ||
        this.loadError
      ) {
        return;
      }
      if (!this.canPublishLifecycle) {
        return;
      }
      if (this.isCreate || this.isDirty) {
        await this.promptSaveBeforeLifecycle("publish");
        return;
      }

      this.showValidation = true;
      const errors = this.validation;
      if (hasValidationErrors(errors)) {
        this.focusFirstError(errors);
        this.$fire({
          title: "Form belum valid",
          text:
            "Perbaiki isian yang ditandai sebelum mempublikasikan form.",
          type: "warning",
          timer: 3200
        });
        return;
      }

      const result = await this.$fire({
        title: "Publikasikan form?",
        text:
          'Form "' +
          (this.formName || "ini") +
          '" akan dipublikasikan dengan schema yang sudah tersimpan. Setelah berhasil, status form menjadi aktif dan schema field terkunci.',
        type: "question",
        showCancelButton: true,
        confirmButtonText: "Ya, publikasikan",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      });
      if (!result || !result.value) {
        return;
      }

      await this.executePublish();
    },
    async executePublish() {
      if (this.lifecycleAction) {
        return;
      }
      this.lifecycleAction = "publish";
      let shouldRetry = false;

      try {
        const response = await this.$store.dispatch(
          "waajoForms/publishWaajoForm",
          { id: this.formId }
        );
        const data = response && response.data ? response.data : null;
        let confirmed = !!(
          data &&
          data.status === "active" &&
          data.schema_version !== undefined
        );

        if (confirmed) {
          this.applyServerMetadata(data);
        } else {
          await this.fetchFormDetail();
          confirmed = this.formStatus === "active";
        }

        if (!confirmed) {
          this.$fire({
            title: "Status form belum terkonfirmasi",
            text:
              "Permintaan publish diterima, tetapi status aktif belum dapat dikonfirmasi dari server. Muat ulang halaman untuk melihat status terbaru.",
            type: "warning"
          });
          return;
        }

        this.savedSnapshot = this.buildSnapshot();
        this.showValidation = false;
        const url = getPublicFormUrl(this.formId);
        const result = await this.$fire({
          title: "Form berhasil dipublikasikan",
          text:
            'Form "' +
            (this.formName || "ini") +
            '" sekarang aktif.\n\nLink publik:\n' +
            url +
            "\n\nHalaman publik akan tersedia pada tahap berikutnya.",
          type: "success",
          showCancelButton: true,
          confirmButtonText: "Salin Link",
          cancelButtonText: "Tutup",
          confirmButtonColor: "#FE9B44"
        });
        if (result && result.value) {
          await this.copyCurrentPublicLink();
        }
      } catch (error) {
        shouldRetry = await this.handleLifecycleError("publish", error);
      } finally {
        this.lifecycleAction = "";
      }

      if (shouldRetry) {
        await this.executePublish();
      }
    },
    async deactivateForm() {
      if (
        this.lifecycleAction ||
        this.saving ||
        this.loading ||
        this.loadError
      ) {
        return;
      }
      if (!this.canDeactivateLifecycle) {
        return;
      }
      if (this.isDirty) {
        await this.promptSaveBeforeLifecycle("deactivate");
        return;
      }

      const result = await this.$fire({
        title: "Nonaktifkan form?",
        text:
          'Form "' +
          (this.formName || "ini") +
          '" akan dinonaktifkan. Form tidak lagi berstatus aktif, tetapi data respons yang sudah tersimpan tidak dihapus. Schema dapat diedit kembali setelah form nonaktif.',
        type: "question",
        showCancelButton: true,
        confirmButtonText: "Ya, nonaktifkan",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      });
      if (!result || !result.value) {
        return;
      }

      await this.executeDeactivate();
    },
    async executeDeactivate() {
      if (this.lifecycleAction) {
        return;
      }
      this.lifecycleAction = "deactivate";
      let shouldRetry = false;

      try {
        const response = await this.$store.dispatch(
          "waajoForms/deactivateWaajoForm",
          { id: this.formId }
        );
        const data = response && response.data ? response.data : null;
        let confirmed = !!(data && data.status === "inactive");

        if (confirmed) {
          this.applyServerMetadata(data);
        } else {
          await this.fetchFormDetail();
          confirmed = this.formStatus === "inactive";
        }

        if (!confirmed) {
          this.$fire({
            title: "Status form belum terkonfirmasi",
            text:
              "Permintaan deactivate diterima, tetapi status nonaktif belum dapat dikonfirmasi dari server. Muat ulang halaman untuk melihat status terbaru.",
            type: "warning"
          });
          return;
        }

        this.savedSnapshot = this.buildSnapshot();
        this.$fire({
          title: "Form dinonaktifkan",
          text:
            'Form "' +
            (this.formName || "ini") +
            '" sekarang nonaktif. Schema field dapat diedit kembali.',
          type: "success",
          timer: 2600
        });
      } catch (error) {
        shouldRetry = await this.handleLifecycleError("deactivate", error);
      } finally {
        this.lifecycleAction = "";
      }

      if (shouldRetry) {
        await this.executeDeactivate();
      }
    },
    async handleLifecycleError(action, error) {
      const isPublish = action === "publish";
      const title = isPublish
        ? "Gagal mempublikasikan form"
        : "Gagal menonaktifkan form";
      const message = this.getErrorMessage(
        error,
        isPublish
          ? "Form belum dapat dipublikasikan. Coba lagi."
          : "Form belum dapat dinonaktifkan. Coba lagi."
      );

      if (isLifecycleConflict(error)) {
        this.$fire({
          title: title,
          text: message + " Status terbaru akan dimuat ulang.",
          type: "error"
        });
        await this.fetchFormDetail();
        return false;
      }

      if (isLifecycleRetryable(error)) {
        const result = await this.$fire({
          title: title,
          text: message,
          type: "error",
          showCancelButton: true,
          confirmButtonText: "Coba Lagi",
          cancelButtonText: "Tutup",
          confirmButtonColor: "#FE9B44"
        });
        return !!(result && result.value);
      }

      this.$fire({
        title: title,
        text: message,
        type: "error"
      });
      return false;
    },
    async saveDraft() {
      if (!this.canSave) {
        return false;
      }
      this.showValidation = true;
      const errors = this.validation;
      if (hasValidationErrors(errors)) {
        this.focusFirstError(errors);
        this.$fire({
          title: "Periksa kembali form Anda",
          text:
            "Masih ada isian yang belum valid. Perbaiki bagian yang ditandai sebelum menyimpan.",
          type: "warning",
          timer: 3200
        });
        return false;
      }

      const payload = buildFormPayload({
        id: this.formId,
        name: this.formName,
        description: this.formDescription,
        fields: this.fields,
        pages: this.pages,
        includeFields: !this.isSchemaLocked
      });

      this.saving = true;
      try {
        if (this.isCreate) {
          const response = await this.$store.dispatch(
            "waajoForms/storedWaajoForm",
            payload
          );
          const data = response && response.data ? response.data : null;
          if (!data || !data.id) {
            this.$fire({
              title: "Respons tidak lengkap",
              text:
                "Form diproses, tetapi ID form tidak diterima. Kembali ke daftar form untuk memeriksa data.",
              type: "warning"
            });
            return false;
          }

          this.formId = data.id;
          this.formStatus = data.status || "draft";
          this.schemaVersion = Number(data.schema_version) || 0;
          this.updateDatetime = Number(data.update_datetime) || 0;
          this.savedSnapshot = this.buildSnapshot();
          this.allowLeave = true;

          this.$fire({
            title: "Berhasil",
            text: "Form berhasil dibuat. Anda dapat melanjutkan mengedit draft.",
            type: "success",
            timer: 2400
          });

          await this.$router.replace({
            name: "waajo_forms_editor",
            params: { id: data.id }
          });
        } else {
          const response = await this.$store.dispatch(
            "waajoForms/updateWaajoForm",
            payload
          );
          const data = response && response.data ? response.data : null;
          if (data) {
            this.applyServerMetadata(data);
          }
          this.savedSnapshot = this.buildSnapshot();

          this.$fire({
            title: "Berhasil",
            text: this.isSchemaLocked
              ? "Perubahan form berhasil disimpan."
              : "Draft form berhasil disimpan.",
            type: "success",
            timer: 2200
          });
        }
        return true;
      } catch (error) {
        this.$fire({
          title: "Gagal menyimpan form",
          text: this.getErrorMessage(
            error,
            "Form belum dapat disimpan. Coba lagi."
          ),
          type: "error"
        });
        return false;
      } finally {
        this.saving = false;
      }
    },
    applyServerMetadata(data) {
      if (!data) {
        return;
      }
      if (typeof data.name === "string") {
        this.formName = data.name;
      }
      if (typeof data.description === "string") {
        this.formDescription = data.description;
      }
      if (typeof data.status === "string") {
        this.formStatus = data.status;
      }
      if (data.schema_version !== undefined) {
        this.schemaVersion = Number(data.schema_version) || 0;
      }
      if (data.update_datetime !== undefined) {
        this.updateDatetime = Number(data.update_datetime) || 0;
      }
    },
    getErrorMessage(error, fallback) {
      if (getHttpStatus(error) === 401) {
        this.handleSessionExpired();
      }
      return getWaajoFormsErrorMessage(error, fallback);
    },
    handleSessionExpired() {
      if (this.sessionExpired) {
        return;
      }
      this.sessionExpired = true;
      const currentPath =
        this.$route && this.$route.fullPath
          ? this.$route.fullPath
          : "/forms";
      this.$store.dispatch("login/resetToken").finally(() => {
        this.$router
          .replace({
            path: "/login",
            query: { redirect: currentPath }
          })
          .catch(() => {});
      });
    },
    onBeforeUnload(event) {
      if ((!this.isDirty && !this.notificationDirty) || this.allowLeave) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
  }
};
</script>

<style scoped>
.wf-editor-page {
  padding-bottom: 32px;
}

.wf-editor-header .page-title {
  margin-bottom: 4px;
}

.wf-editor-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

.wf-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #64748b;
  font-size: 12px;
}

.wf-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
}

.wf-status-dot {
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: currentColor;
}

.wf-status-draft {
  background: #f1f5f9;
  color: #475569;
}

.wf-status-active {
  background: #e8f8ee;
  color: #18753c;
}

.wf-status-inactive {
  background: #fff4e5;
  color: #a15c00;
}

.wf-editor-actions {
  margin-top: 8px;
}

.wf-save-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.wf-save-state.is-dirty {
  color: #b45309;
}

.wf-save-state.is-saving {
  color: #2563eb;
}

.wf-save-state.is-saved {
  color: #15803d;
}

.wf-editor-buttons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.wf-primary-btn {
  background-color: #fe9b44;
  border-color: #fe9b44;
  color: #ffffff;
}

.wf-primary-btn:hover,
.wf-primary-btn:focus {
  background-color: #f08a2e;
  border-color: #f08a2e;
  color: #ffffff;
}

.wf-primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.wf-publish-btn {
  background-color: #16a34a;
  border-color: #16a34a;
  color: #ffffff;
}

.wf-publish-btn:hover,
.wf-publish-btn:focus {
  background-color: #15803d;
  border-color: #15803d;
  color: #ffffff;
}

.wf-publish-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.wf-deactivate-btn {
  border-color: #f59e0b;
  background: #fffbeb;
  color: #b45309;
}

.wf-deactivate-btn:hover,
.wf-deactivate-btn:focus {
  border-color: #d97706;
  background: #fef3c7;
  color: #92400e;
}

.wf-deactivate-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.wf-state-card {
  border: 1px solid #eef1f5;
  border-radius: 12px;
}

.wf-state-card .card-body {
  padding: 52px 20px;
}

.wf-state-spinner {
  color: #fe9b44;
  font-size: 30px;
}

.wf-state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  font-size: 26px;
}

.wf-state-icon.is-error {
  background: #fef2f2;
  color: #dc2626;
}

.wf-state-text {
  max-width: 520px;
  margin: 0 auto 20px;
}

.wf-builder-shell {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;
}

.wf-panel {
  min-width: 0;
  border: 1px solid #eef1f5;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
}

.wf-palette-panel,
.wf-props-panel {
  position: sticky;
  top: 76px;
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  padding: 16px;
}

.wf-canvas-panel {
  padding: 16px;
}

.wf-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.wf-panel-head h5 {
  margin: 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 700;
}

.wf-panel-kicker {
  display: block;
  margin-bottom: 2px;
  color: #fe9b44;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.wf-count-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border: 1px solid #fde7d1;
  border-radius: 999px;
  background: #fff7ef;
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.wf-panel-hint {
  margin-bottom: 12px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.55;
}

.wf-lock-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 12px;
  padding: 10px 11px;
  border: 1px solid #fde7d1;
  border-radius: 8px;
  background: #fffaf5;
  color: #9a5b1d;
  font-size: 12px;
  line-height: 1.5;
}

.wf-lock-note i {
  margin-top: 2px;
}

.wf-palette-grid {
  display: grid;
  gap: 8px;
}

.wf-palette-item {
  position: relative;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  grid-template-rows: auto auto;
  align-items: center;
  gap: 2px 9px;
  width: 100%;
  padding: 10px 28px 10px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #ffffff;
  text-align: left;
  cursor: grab;
  transition: border-color 0.16s ease, box-shadow 0.16s ease,
    transform 0.16s ease, background 0.16s ease;
}

.wf-palette-item:hover:not(:disabled),
.wf-palette-item:focus:not(:disabled) {
  border-color: #fe9b44;
  background: #fffaf5;
  box-shadow: 0 4px 12px rgba(254, 155, 68, 0.14);
  outline: none;
  transform: translateY(-1px);
}

.wf-palette-item:active:not(:disabled) {
  cursor: grabbing;
}

.wf-palette-item.is-disabled,
.wf-palette-item:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.wf-palette-icon {
  display: inline-flex;
  grid-row: 1 / span 2;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fff4e8;
  color: #d97706;
  font-size: 14px;
}

.wf-palette-name {
  color: #1f2937;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
}

.wf-palette-desc {
  color: #94a3b8;
  font-size: 10px;
  line-height: 1.35;
}

.wf-palette-grip {
  position: absolute;
  top: 50%;
  right: 9px;
  color: #cbd5e1;
  font-size: 11px;
  transform: translateY(-50%);
}

.wf-palette-footnote {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eef1f5;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.5;
}

.wf-canvas-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.wf-canvas-toolbar h5 {
  margin: 0;
  color: #1f2937;
  font-size: 17px;
  font-weight: 700;
}

.wf-canvas-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.wf-drag-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
}

.wf-lock-banner {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 9px;
  margin-bottom: 14px;
  padding: 11px 13px;
  border: 1px solid #fde7d1;
  border-radius: 9px;
  background: #fffaf5;
  color: #7c4a13;
  font-size: 12px;
  line-height: 1.55;
}

.wf-lock-banner i {
  margin-top: 2px;
  color: #d97706;
}

.wf-lock-banner-content {
  flex: 1 1 260px;
  min-width: 0;
}

.wf-public-link {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 6px;
  color: #92400e;
  font-family: Consolas, Monaco, monospace;
  font-size: 11px;
  word-break: break-all;
}

.wf-public-link i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.wf-public-link-url {
  min-width: 0;
}

.wf-public-link-note {
  display: block;
  margin-top: 3px;
  color: #a16207;
  font-size: 10px;
}

.wf-public-link-btn {
  flex: 0 0 auto;
  align-self: center;
  border-color: #fcd34d;
  background: #ffffff;
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
}

.wf-public-link-btn:hover,
.wf-public-link-btn:focus {
  border-color: #f59e0b;
  background: #fef3c7;
  color: #92400e;
}

.wf-canvas-form {
  position: relative;
  padding: 18px;
  border: 1px dashed #dbe3ec;
  border-radius: 11px;
  background: #fbfcfe;
  transition: border-color 0.16s ease, background 0.16s ease;
}

.wf-canvas-form--dragging {
  border-color: #fe9b44;
  background: #fffaf5;
}

.wf-canvas-form--dragging .wf-field-card > * {
  pointer-events: none;
}

.wf-canvas-form--dragging .wf-drop-indicator {
  pointer-events: none;
}

.wf-canvas-form--preview {
  border-style: solid;
  border-color: #eef1f5;
  background: #ffffff;
}

.wf-form-meta {
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #eef1f5;
  border-radius: 9px;
  background: #ffffff;
}

.wf-form-meta .wf-form-group:last-child {
  margin-bottom: 0;
}

.wf-form-group {
  margin-bottom: 14px;
}

.wf-form-group > label,
.wf-options-head label {
  display: block;
  margin-bottom: 5px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.wf-form-group--meta > label {
  color: #334155;
  font-size: 13px;
}

.wf-form-group .form-control,
.wf-option-card .form-control {
  border-color: #e2e8f0;
  border-radius: 7px;
  font-size: 13px;
}

.wf-form-group .form-control:focus,
.wf-option-card .form-control:focus {
  border-color: #fe9b44;
  box-shadow: 0 0 0 0.16rem rgba(254, 155, 68, 0.16);
}

.wf-description-input {
  height: auto;
  min-height: 76px;
  resize: vertical;
}

.wf-input-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-top: 3px;
}

.wf-char-count {
  margin-left: auto;
  color: #94a3b8;
  font-size: 10px;
  white-space: nowrap;
}

.wf-required {
  color: #dc2626;
  font-weight: 700;
}

.wf-error {
  margin-top: 4px;
  color: #dc2626;
  font-size: 11px;
  line-height: 1.45;
}

.wf-empty-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 270px;
  padding: 32px 20px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  text-align: center;
  transition: border-color 0.16s ease, background 0.16s ease;
}

.wf-empty-canvas.is-drop-target {
  border-color: #fe9b44;
  background: #fff7ef;
}

.wf-empty-canvas-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 12px;
  border-radius: 50%;
  background: #fff4e8;
  color: #fe9b44;
  font-size: 25px;
}

.wf-empty-canvas h5 {
  margin-bottom: 5px;
  color: #334155;
}

.wf-empty-canvas p {
  max-width: 400px;
  margin-bottom: 10px;
  font-size: 13px;
}

.wf-empty-drop-label {
  display: inline-flex;
  padding: 4px 9px;
  border-radius: 999px;
  background: #fe9b44;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
}

.wf-empty-canvas--preview {
  min-height: 220px;
}

.wf-fields-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wf-field-wrap {
  position: relative;
}

.wf-drop-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  margin: 2px 0;
  color: #d97706;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.wf-drop-indicator::before {
  content: "";
  flex: 1 1 auto;
  height: 2px;
  border-radius: 999px;
  background: #fe9b44;
}

.wf-drop-indicator::after {
  content: "";
  flex: 1 1 auto;
  height: 2px;
  border-radius: 999px;
  background: #fe9b44;
}

.wf-field-card {
  position: relative;
  padding: 10px 12px 12px;
  border: 1px solid #e5eaf1;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;
}

.wf-field-card:hover {
  border-color: #f7c795;
}

.wf-field-card.is-selected {
  border-color: #fe9b44;
  box-shadow: 0 0 0 2px rgba(254, 155, 68, 0.17);
}

.wf-field-card.is-preview {
  padding: 4px 2px 14px;
  border: 0;
  border-bottom: 1px solid #eef1f5;
  border-radius: 0;
  background: transparent;
  cursor: default;
}

.wf-field-card.is-preview:last-child {
  border-bottom: 0;
}

.wf-field-card.is-dragging {
  opacity: 0.5;
}

.wf-field-card.has-validation-error {
  border-color: #fca5a5;
}

.wf-field-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
}

.wf-field-toolbar-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.wf-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  color: #94a3b8;
  font-size: 11px;
  cursor: grab;
}

.wf-drag-handle:hover {
  background: #fff4e8;
  color: #d97706;
}

.wf-drag-handle:active {
  cursor: grabbing;
}

.wf-field-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 5px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
}

.wf-field-type-icon {
  color: #fe9b44;
  font-size: 11px;
}

.wf-field-type-label {
  overflow: hidden;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wf-field-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.wf-field-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  color: #94a3b8;
  font-size: 11px;
}

.wf-field-action:hover:not(:disabled),
.wf-field-action:focus:not(:disabled) {
  border-color: #fde7d1;
  background: #fff7ef;
  color: #d97706;
  outline: none;
}

.wf-field-action.is-danger:hover:not(:disabled),
.wf-field-action.is-danger:focus:not(:disabled) {
  border-color: #fecaca;
  background: #fef2f2;
  color: #dc2626;
}

.wf-field-action:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.wf-field-content {
  min-width: 0;
}

.wf-field-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.wf-field-label {
  margin: 0;
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}

.wf-field-key {
  overflow: hidden;
  color: #94a3b8;
  font-family: Consolas, Monaco, monospace;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wf-field-error {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 6px;
  color: #dc2626;
  font-size: 11px;
  line-height: 1.45;
}

.wf-field-error i {
  margin-top: 2px;
}

.wf-canvas-tail {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 8px;
  border: 1px dashed #dbe3ec;
  border-radius: 8px;
  color: #94a3b8;
  font-size: 10px;
  text-align: center;
  transition: border-color 0.16s ease, background 0.16s ease,
    color 0.16s ease;
}

.wf-canvas-tail.is-drop-target {
  border-color: #fe9b44;
  background: #fff7ef;
  color: #b45309;
}

.wf-general-error {
  margin-top: 14px;
  margin-bottom: 0;
  font-size: 12px;
}

.wf-props-empty {
  padding: 26px 8px;
  text-align: center;
}

.wf-props-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: #fff4e8;
  color: #fe9b44;
  font-size: 19px;
}

.wf-props-empty h6 {
  margin-bottom: 5px;
  color: #334155;
}

.wf-props-empty p {
  font-size: 11px;
  line-height: 1.55;
}

.wf-input-action {
  display: flex;
  align-items: stretch;
  gap: 6px;
}

.wf-input-action .form-control {
  min-width: 0;
}

.wf-input-action .btn {
  flex: 0 0 auto;
  padding: 4px 8px;
  border-color: #e2e8f0;
  border-radius: 7px;
  color: #64748b;
  font-size: 10px;
  white-space: nowrap;
}

.wf-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0;
  padding: 9px 10px;
  border: 1px solid #eef1f5;
  border-radius: 8px;
  background: #fbfcfe;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.wf-switch {
  position: relative;
  display: inline-flex;
  flex: 0 0 38px;
  width: 38px;
  height: 21px;
}

.wf-switch input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.wf-switch-slider {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.16s ease;
}

.wf-switch-slider::before {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
  transition: transform 0.16s ease;
}

.wf-switch input:checked + .wf-switch-slider {
  background: #fe9b44;
}

.wf-switch input:checked + .wf-switch-slider::before {
  transform: translateX(17px);
}

.wf-switch input:disabled + .wf-switch-slider {
  opacity: 0.55;
}

.wf-options-section {
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #eef1f5;
}

.wf-options-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.wf-option-card {
  margin-bottom: 9px;
  padding: 9px;
  border: 1px solid #eef1f5;
  border-radius: 8px;
  background: #fbfcfe;
}

.wf-option-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.wf-option-card > label {
  display: block;
  margin: 5px 0 3px;
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
}

.wf-option-value-label {
  margin-top: 7px !important;
}

.wf-option-card .form-control {
  height: 34px;
  padding: 4px 8px;
  font-size: 12px;
}

@media (max-width: 1199.98px) {
  .wf-builder-shell {
    grid-template-columns: 190px minmax(0, 1fr);
  }

  .wf-props-panel {
    position: static;
    grid-column: 1 / -1;
    max-height: none;
  }
}

@media (max-width: 991.98px) {
  .wf-builder-shell {
    display: block;
  }

  .wf-palette-panel,
  .wf-props-panel {
    position: static;
    max-height: none;
    margin-bottom: 14px;
  }

  .wf-canvas-panel {
    margin-bottom: 14px;
  }

  .wf-palette-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .wf-editor-actions {
    margin-top: 10px;
    text-align: left !important;
  }
}

@media (max-width: 575.98px) {
  .wf-palette-grid {
    display: flex;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .wf-palette-item {
    flex: 0 0 168px;
  }

  .wf-canvas-toolbar {
    flex-direction: column;
  }

  .wf-canvas-toolbar-right {
    justify-content: flex-start;
  }

  .wf-editor-buttons {
    display: flex;
    width: 100%;
  }

  .wf-editor-buttons .btn {
    flex: 1 1 0;
  }

  .wf-canvas-form {
    padding: 12px;
  }

  .wf-field-toolbar {
    align-items: flex-start;
  }

  .wf-field-type-label {
    display: none;
  }

  .wf-field-label-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 2px;
  }

  .wf-field-key {
    max-width: 100%;
  }
}

.wf-editor-tabs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  margin-bottom: 18px;
  padding: 4px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.035);
}

.wf-editor-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.wf-editor-tab:hover,
.wf-editor-tab:focus {
  background: #f8fafc;
  color: #b45309;
  outline: none;
}

.wf-editor-tab.is-active {
  background: #fff3e6;
  color: #b45309;
}

.wf-settings-shell {
  margin-bottom: 24px;
}

.wf-settings-card {
  margin-bottom: 0;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 6px 22px rgba(15, 23, 42, 0.04);
}

.wf-settings-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #eef2f7;
}

.wf-settings-head h4 {
  margin: 2px 0 6px;
  color: #1e293b;
  font-size: 18px;
  font-weight: 700;
}

.wf-settings-body {
  padding: 22px;
}

.wf-image-layout {
  display: flex;
  align-items: flex-start;
  gap: 26px;
}

.wf-image-preview-box {
  position: relative;
  flex: 0 0 310px;
  min-height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px dashed #dbe3ec;
  border-radius: 12px;
  background: #fbfcfe;
}

.wf-form-image-preview {
  display: block;
  width: 100%;
  max-height: 280px;
  object-fit: contain;
  background: #ffffff;
}

.wf-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
}

.wf-image-placeholder i {
  font-size: 28px;
  color: #cbd5e1;
}

.wf-image-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.82);
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
}

.wf-image-controls {
  flex: 1 1 0;
  min-width: 0;
}

.wf-image-controls h5 {
  margin-bottom: 6px;
  color: #334155;
  font-size: 15px;
  font-weight: 700;
}

.wf-image-controls p {
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1.6;
}

.wf-settings-hint {
  display: block;
  margin-top: 10px;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.55;
}

.wf-file-meta {
  margin-top: 10px;
  color: #64748b;
  font-size: 11px;
  overflow-wrap: anywhere;
}

.wf-image-controls .wf-settings-alert {
  margin: 12px 0;
}

.wf-settings-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 9px;
  font-size: 12px;
  line-height: 1.5;
}

.wf-settings-message {
  padding: 44px 24px;
  text-align: center;
}

.wf-settings-message h5 {
  margin-bottom: 6px;
  color: #1e293b;
  font-weight: 700;
}

.wf-notification-row {
  margin-top: 2px;
}

.wf-notification-card {
  height: auto;
  margin-bottom: 0;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.03);
}

.wf-notification-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 16px 10px;
  border-bottom: 1px solid #eef2f7;
}

.wf-notification-head h4 {
  margin: 0 0 3px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
}

.wf-notification-type-label {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 7px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  color: #64748b;
  font-size: 9px;
  font-weight: 700;
  vertical-align: middle;
}

.wf-notification-recipient-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 10px;
  padding: 7px 9px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f8fbff;
  color: #475569;
  font-size: 10.5px;
  line-height: 1.45;
}

.wf-notification-config-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

.wf-notification-config-grid.is-single {
  grid-template-columns: 1fr;
}

.wf-notification-config-grid .form-group {
  min-width: 0;
  margin-bottom: 10px;
}

.wf-notification-recipient-note.is-invalid {
  border-color: #fecaca;
  background: #fff7f7;
  color: #b91c1c;
}

.wf-notification-recipient-note .fa {
  margin-top: 2px;
  color: #64748b;
}

.wf-notification-recipient-note.is-invalid .fa {
  color: #b91c1c;
}

.wf-notification-head p {
  font-size: 12px;
  line-height: 1.5;
}

.wf-optional-badge {
  flex: 0 0 auto;
  padding: 3px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  color: #64748b;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.wf-notification-body {
  padding: 12px 16px 14px;
}

.wf-notification-body .form-group {
  margin-bottom: 10px;
}

.wf-notification-body .form-group > label {
  margin-bottom: 4px;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

.wf-notification-body .text-muted {
  font-size: 10px;
  line-height: 1.45;
}

.wf-notification-body .custom-control-label {
  color: #334155;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.wf-notification-template-group {
  margin-top: 2px;
}

.wf-notification-textarea {
  min-height: 86px;
  resize: vertical;
  font-size: 12px;
  line-height: 1.5;
}

.wf-template-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}

.wf-template-head label {
  margin: 0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

.wf-template-reset {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #ffffff;
  color: #64748b;
  font-size: 9px;
  cursor: pointer;
}

.wf-template-reset:hover:not(:disabled) {
  border-color: #f4b15d;
  background: #fff8ec;
  color: #9a5d00;
}

.wf-template-reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wf-wa-format-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 5px;
}

.wf-wa-format-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 24px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
}

.wf-wa-format-btn:hover:not(:disabled) {
  border-color: #f4b15d;
  background: #fff8ec;
  color: #9a5d00;
}

.wf-wa-format-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wf-wa-format-btn.is-bold {
  font-weight: 800;
}

.wf-wa-format-btn.is-italic {
  font-style: italic;
}

.wf-wa-format-btn.is-strike {
  text-decoration: line-through;
}

.wf-wa-format-btn.is-mono {
  font-family: Consolas, monospace;
}

.wf-template-tokens {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 78px;
  margin-bottom: 6px;
  padding: 6px 7px;
  overflow-y: auto;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  background: #fbfcfe;
}

.wf-template-tokens-label {
  margin-right: 2px;
  color: #94a3b8;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.wf-template-token {
  padding: 3px 7px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  font-size: 9px;
  line-height: 1.2;
  cursor: pointer;
}

.wf-template-token:hover:not(:disabled),
.wf-template-token:focus:not(:disabled) {
  border-color: #f4b15d;
  background: #fff8ec;
  color: #9a5d00;
  outline: none;
}

.wf-template-token:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wf-owner-recipient {
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #eef2f7;
  border-radius: 9px;
  background: #fbfcfe;
}

.wf-owner-recipient > span {
  display: block;
  margin-bottom: 3px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.wf-owner-recipient > strong {
  display: block;
  color: #334155;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.wf-owner-recipient > small {
  display: block;
  margin-top: 5px;
  font-size: 10px;
  line-height: 1.5;
}

.wf-settings-error {
  display: block;
  margin-top: 5px;
  color: #b91c1c;
  font-size: 11px;
  line-height: 1.5;
}

.wf-message-preview {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #fde3c2;
  border-radius: 9px;
  background: #fffaf3;
}

.wf-message-preview > span {
  display: block;
  margin-bottom: 6px;
  color: #b45309;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.wf-message-preview p {
  margin: 0 0 3px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.wf-message-preview p:last-child {
  margin-bottom: 0;
}

.wf-field-checks {
  max-height: 190px;
  overflow-y: auto;
  padding: 7px;
  border: 1px solid #e8edf3;
  border-radius: 9px;
  background: #fbfcfe;
}

.wf-field-checks.is-disabled {
  opacity: 0.6;
}

.wf-check-item {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0;
  padding: 5px 6px;
  border-radius: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.wf-check-item:hover {
  background: #f1f5f9;
}

.wf-check-item input {
  margin-top: 2px;
}

.wf-check-item small {
  display: block;
  margin-top: 1px;
  color: #94a3b8;
  font-size: 10px;
  font-family: monospace;
}

.wf-inline-link {
  padding: 0;
  border: 0;
  color: #b45309;
  font-size: 10px;
  font-weight: 600;
  text-decoration: underline;
  vertical-align: baseline;
}

.wf-inline-link:hover,
.wf-inline-link:focus {
  color: #92400e;
  text-decoration: underline;
}

.wf-settings-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

@media (max-width: 991.98px) {
  .wf-image-layout {
    flex-direction: column;
  }

  .wf-image-preview-box {
    width: 100%;
    flex-basis: auto;
  }
}

@media (max-width: 575.98px) {
  .wf-editor-tabs {
    display: flex;
    width: 100%;
    overflow-x: auto;
  }

  .wf-editor-tab {
    flex: 1 0 auto;
    justify-content: center;
    padding: 8px 10px;
    font-size: 12px;
  }

  .wf-settings-head,
  .wf-settings-body,
  .wf-notification-head,
  .wf-notification-body {
    padding-left: 15px;
    padding-right: 15px;
  }

  .wf-settings-actions {
    flex-direction: column-reverse;
  }

  .wf-settings-actions .btn {
    width: 100%;
  }
}

.wf-page-strip {
  margin: 14px 0 0;
  padding: 12px;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  background: #fbfcff;
}
.wf-page-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.wf-page-tab,
.wf-page-add {
  flex: 0 0 auto;
  min-width: 130px;
  padding: 8px 12px;
  border: 1px solid #dce2ee;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  color: #44506a;
}
.wf-page-tab strong,
.wf-page-tab small,
.wf-page-tab-label {
  display: block;
}
.wf-page-tab-label {
  font-size: 11px;
  color: #8792a8;
}
.wf-page-tab.is-active {
  border-color: #fe9b44;
  box-shadow: 0 0 0 2px rgba(254, 155, 68, 0.14);
}
.wf-page-add {
  border-style: dashed;
  text-align: center;
  background: #fffaf4;
}
.wf-page-add:disabled {
  opacity: 0.55;
}
.wf-page-strip-foot {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: #8792a8;
}
.wf-page-settings {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 10px;
}
.wf-page-title-field {
  flex: 1;
  margin: 0;
}
.wf-page-title-field span {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: #68748b;
}

.wf-page-title-field span small {
  color: #94a3b8;
  font-size: 10px;
  font-weight: 500;
}
.wf-page-actions {
  display: flex;
  gap: 4px;
}
@media (max-width: 767px) {
  .wf-page-settings {
    align-items: stretch;
    flex-direction: column;
  }
  .wf-page-tab,
  .wf-page-add {
    min-width: 115px;
  }
}

.wf-notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}
.wf-notification-header h5 {
  margin-bottom: 4px;
}
.wf-add-notification {
  margin-top: 8px;
}
.wf-settings-empty {
  padding: 24px;
  border: 1px dashed #dbe1ec;
  border-radius: 12px;
  text-align: center;
  color: #8792a8;
  background: #fbfcff;
}
.wf-notification-preview {
  margin-top: 7px;
  padding: 8px 10px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  background: #f7f9fc;
  white-space: pre-wrap;
  font-size: 11px;
  line-height: 1.5;
}
.wf-notification-preview strong {
  color: #64748b;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.wf-notification-preview p {
  margin: 3px 0 0;
  color: #475569;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.wf-wa-preview {
  margin-top: 8px;
  padding: 8px;
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  background:
    radial-gradient(circle at 10% 10%, rgba(254, 155, 68, 0.08), transparent 34%),
    #f3f5f7;
}

.wf-wa-preview-label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.wf-wa-preview-label .fa {
  color: #25d366;
  font-size: 12px;
}

.wf-wa-bubble {
  display: block;
  width: fit-content;
  max-width: 96%;
  padding: 8px 10px;
  border: 1px solid #f3dfbf;
  border-radius: 10px 10px 10px 3px;
  background: #fff9ef;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
}

.wf-wa-text {
  color: #344054;
  font-size: 11.5px;
  line-height: 1.55;
  overflow-wrap: anywhere;
  word-break: break-word;
}
@media (max-width: 767px) {
  .wf-notification-config-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
@media (max-width: 575px) {
  .wf-notification-header {
    flex-direction: column;
  }
  .wf-notification-head {
    flex-wrap: wrap;
  }
  .wf-notification-body {
    padding-left: 12px;
    padding-right: 12px;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/forms/editor.vue